import {wallToInstant} from '../utils/businessTime';
// LOOP AGENDA IOS SERVER-SCOPED BOOKINGS RENDER V16J — server-filtered iOS worker bookings bypass stale email selectedSet render filters.
// LOOP AGENDA IOS BACKEND IDENTITY V16G — worker email resolves to real employeeId/profile/bookings via Functions.
// LOOP AGENDA IOS WORKER IDENTITY V16F — iOS email-mode hydrates worker employeeId/profile before final paint.
// LOOP AGENDA IOS EMAIL SAFE PAINT V16E — bookings fallback signal can unlock email-mode worker agenda.
// LOOP AGENDA IOS LOADER FALLBACK V16D — robust Capacitor detection, function preflight debug, and anti-infinite-loader support.
// LOOP AGENDA CORRECT FIRST PAINT V16C — iOS worker bookings via Function fallback + rich-profile-first debug.
// LOOP AGENDA HYDRATION STABILITY V16B — persist/use rich worker profile and make iOS booking hydration debuggable.
// LOOP WORKER AGENDA IOS FIRST PAINT V10C — native function call + no iOS Web SDK worker queries.
// LOOP WORKER LOADER LOOP FIX V10B — worker identity changes drive bookings; bookings refresh must not reload worker profile.
// LOOP WORKER AGENDA INITIAL LOAD V10 — function-first worker profile/services; avoid denied reads/flicker.
// LOOP AGENDA MADRE WORKER PROFILE PARITY V93 — Cloud Function employee profile enrichment for worker agenda photo/availability.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — targeted worker loader + employee-doc enrichment from workerAccess mirror.
// LOOP AGENDA MADRE WORKER HYDRATION RACE V85 — preserve real worker route/access profile when employees collection is denied or late.
// LOOP AGENDA MADRE WORKER RESOURCE PARITY V84 — route-state worker profile fills real employee resource when employee doc is late/limited.
 // LOOP AGENDA MADRE WORKER SETTINGS/AVAILABILITY V83 — data contract unchanged; included for safe replacement.
 // LOOP AGENDA MADRE WORKER AVAILABILITY/PENDINGS HOTFIX V82 — unchanged from V81; included for safe replacement.
// LOOP AGENDA MADRE WORKER AVAILABILITY/PENDINGS V81 — badge contract remains employeeId-only, paired with WorkerPendings V81.
// LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80 — worker badges count employeeId-assigned reservations only.
// LOOP AGENDA MADRE WORKER POLISH V79 — adds worker-scoped pending counts for sidebar/mobile badges.
// LOOP AGENDA MADRE CLEANUP D7 V78 — responsive cleanup validated; data/view contracts unchanged.
// LOOP AGENDA MADRE CLEANUP D6 V77 — compact duplicate event-card/toolbar CSS after D5 validation.
// LOOP AGENDA MADRE CLEANUP D5 V76 — agenda data/view config unchanged; paired with native toolbar cleanup.
// src/adminCalendar/useAdminCalendarData.js
// LOOP AGENDA MADRE CLEANUP D3 V74 — data contract unchanged; cleanup is in render/debug layer.
// LOOP AGENDA MADRE CLEANUP D2 V73 — data contract unchanged; cleanup removed only dead visual surfaces.
// LOOP AGENDA MADRE CLEANUP D1 V72 — retained as the single agenda data/filtering contract.
// LOOP AGENDA MADRE PHASE C V71 — month filtering uses the same owner/worker/team scope.
// LOOP AGENDA MADRE PHASE B V69 — worker/team week filtering uses the same agenda madre contract.
// LOOP AGENDA MADRE PHASE A V67 — worker own/team data filtering supports the unified day grid.
// IMPORTANT: save as UTF-8 without BOM.
// LOOP WORKER PERMISSIONS SAVE HOTFIX — iOS employee list refreshes after worker save.
// LOOP WORKER ACCESS V1 — worker identity is UID/email only; phone/WhatsApp is contact only.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  collectionGroup,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  where,
} from "firebase/firestore";

import fsClient from "../data/firestoreClient";
import { isIosCapacitor, httpRequestJson, getCapacitorRuntimeDebug } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid, getManualAuthStatus } from "../utils/manualAuth";

import {
  startOfDay,
  endOfDayExclusive,
  startOfWeek,
  endOfWeekExclusiveFromStart,
  bookingCalendarDate as bookingScheduledAtToDate,
  colorForService,
} from "../utils/dateUtils";
import { DEFAULT_WORKER_PERMISSIONS, normalizeWorkerPermissions } from "./workerPermissions";

function textBlack() {
  return "#0f172a";
}

function norm(v) {
  return (v || "").toString().trim().toLowerCase();
}


function getWorkerCalendarManualAuthDebugV16D() {
  try {
    const status = getManualAuthStatus();
    const ma = getManualAuth();
    return {
      ...(status || { exists: false, valid: false, reason: "status-error" }),
      hasIdToken: Boolean(ma?.idToken),
      hasUid: Boolean(ma?.uid),
      hasEmail: Boolean(ma?.email),
      source: ma?.source || null,
    };
  } catch (err) {
    return { exists: false, valid: false, reason: "status-exception", error: err?.message || String(err) };
  }
}

function publishIosAgendaDebugV16D(stage, payload = {}) {
  try {
    const debug = {
      version: "LOOP AGENDA IOS LOADER FALLBACK V16D",
      stage,
      native: isIosCapacitor(),
      runtime: getCapacitorRuntimeDebug(),
      manualAuth: getWorkerCalendarManualAuthDebugV16D(),
      ...(payload || {}),
      at: new Date().toISOString(),
    };
    if (typeof window !== "undefined") {
      window.__LOOP_IOS_AGENDA_V16D_DEBUG__ = debug;
      window.__LOOP_IOS_WORKER_BOOKINGS_DEBUG__ = {
        ...(window.__LOOP_IOS_WORKER_BOOKINGS_DEBUG__ || {}),
        v16dStage: stage,
        v16dAt: debug.at,
        v16d: debug,
      };
      try {
        window.dispatchEvent(new CustomEvent("loop-ios-agenda-debug", { detail: debug }));
      } catch (_) {}
    }
    return debug;
  } catch (_) {
    return { version: "LOOP AGENDA IOS LOADER FALLBACK V16D", stage, error: "debug_publish_failed" };
  }
}

function getEffectiveActor(auth) {
  try {
    const u = auth && auth.currentUser ? auth.currentUser : null;
    if (u && (u.uid || u.email)) {
      return {
        uid: u.uid ? String(u.uid) : null,
        email: norm(u.email),
      };
    }
  } catch (_) {}

  try {
    if (isIosCapacitor()) {
      const ma = getManualAuth();
      if (ma && isManualAuthLikelyValid(ma)) {
        return {
          uid: ma.uid ? String(ma.uid) : null,
          email: norm(ma.email),
        };
      }
    }
  } catch (_) {}

  return { uid: null, email: "" };
}


// LOOP AGENDA MADRE WORKER PROFILE PARITY V93
// Worker agenda cannot safely depend on direct employee-doc reads, because worker
// Firestore rules may deny businesses/{businessId}/employees/{employeeId}.
// This backend resolver returns only the current worker's own calendar profile
// after validating workerAccess/workerAccessEmails with Admin SDK.
function cloudFunctionsBaseUrlFromDb(dbInstance) {
  const envBase = String(
    (typeof process !== "undefined" && process.env && (
      process.env.REACT_APP_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_CLOUD_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL
    )) || ""
  ).trim().replace(/\/+$/, "");
  if (envBase) return envBase;

  const projectId = String(dbInstance?.app?.options?.projectId || "").trim();
  if (!projectId) return "";
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

function getWorkerCalendarProfileFunctionUrl(dbInstance) {
  const base = cloudFunctionsBaseUrlFromDb(dbInstance);
  return base ? `${base}/getWorkerCalendarProfileHttp` : "";
}

function getWorkerCalendarBookingsFunctionUrl(dbInstance) {
  const base = cloudFunctionsBaseUrlFromDb(dbInstance);
  return base ? `${base}/getWorkerCalendarBookingsHttp` : "";
}

function workerCalendarPromiseWithTimeout(promise, ms = 10000, label = "worker_calendar_timeout") {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(label)), ms);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}


// LOOP AGENDA INSTANT PAINT V16 — short-lived worker profile cache.
// Worker agenda can mount multiple times in dev/native routing; avoid calling
// getWorkerCalendarProfileHttp repeatedly for the same business/employee.
const LOOP_WORKER_PROFILE_CACHE_V16 = new Map();
const LOOP_WORKER_PROFILE_CACHE_TTL_MS_V16 = 1000 * 60 * 5;

const LOOP_WORKER_PROFILE_LOCAL_CACHE_PREFIX_V16B = "loop_worker_calendar_profile_v16b";

function workerProfileLocalCacheKeyV16B(businessId, employeeId) {
  return `${LOOP_WORKER_PROFILE_LOCAL_CACHE_PREFIX_V16B}:${String(businessId || "").trim()}:${String(employeeId || "").trim()}`;
}

function readWorkerProfileLocalCacheV16B(businessId, employeeId) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(workerProfileLocalCacheKeyV16B(businessId, employeeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at || 0);
    const profile = parsed?.profile && typeof parsed.profile === "object" ? parsed.profile : null;
    if (!profile || !at || Date.now() - at > LOOP_WORKER_PROFILE_CACHE_TTL_MS_V16) return null;
    return { at, profile };
  } catch (_) {
    return null;
  }
}

function writeWorkerProfileLocalCacheV16B(businessId, employeeId, profile) {
  try {
    if (typeof window === "undefined" || !window.localStorage || !profile) return;
    window.localStorage.setItem(
      workerProfileLocalCacheKeyV16B(businessId, employeeId),
      JSON.stringify({ at: Date.now(), profile })
    );
  } catch (_) {}
}

async function getWorkerCalendarProfileIdToken(authObj = null, options = {}) {
  const allowManualTokenFallback = options.allowManualTokenFallback === true;
  try {
    const user = authObj?.currentUser || null;
    if (user && typeof user.getIdToken === "function") {
      const token = await user.getIdToken(false);
      if (token) {
        try {
          publishIosAgendaDebugV16D(options.debugStage || "id_token_from_firebase_user_v16d", {
            tokenSource: "firebase-currentUser",
            hasToken: true,
            authUid: user.uid || null,
            authEmail: user.email || null,
          });
        } catch (_) {}
        return token;
      }
    }
  } catch (err) {
    try {
      publishIosAgendaDebugV16D(options.debugStage || "id_token_firebase_user_failed_v16d", {
        tokenSource: "firebase-currentUser",
        hasToken: false,
        error: err?.message || String(err),
      });
    } catch (_) {}
  }

  try {
    const ma = getManualAuth();
    const manualValid = ma && isManualAuthLikelyValid(ma);
    const token = ma?.idToken || ma?.token || ma?.accessToken || ma?.authToken || "";
    if (token && (manualValid || allowManualTokenFallback)) {
      try {
        publishIosAgendaDebugV16D(options.debugStage || "id_token_from_manual_auth_v16d", {
          tokenSource: manualValid ? "manual-auth-valid" : "manual-auth-fallback-server-will-verify",
          hasToken: true,
          manualValid: Boolean(manualValid),
        });
      } catch (_) {}
      return token;
    }
    try {
      publishIosAgendaDebugV16D(options.debugStage || "id_token_missing_v16d", {
        tokenSource: "none",
        hasToken: false,
        manualExists: Boolean(ma),
        manualValid: Boolean(manualValid),
        allowManualTokenFallback,
      });
    } catch (_) {}
  } catch (err) {
    try {
      publishIosAgendaDebugV16D(options.debugStage || "id_token_manual_auth_failed_v16d", {
        tokenSource: "manual-auth",
        hasToken: false,
        error: err?.message || String(err),
      });
    } catch (_) {}
  }

  return "";
}

async function loadWorkerCalendarProfileViaFunction({ db: dbInstance, auth: authObj, businessId, employeeId, workerEmail = "", fresh = false }) {
  const cleanBusinessId = String(businessId || "").trim();
  const rawEmployeeId = String(employeeId || "").trim();
  const cleanEmployeeId = rawEmployeeId && !rawEmployeeId.startsWith("email:") ? rawEmployeeId : "";
  const cleanWorkerEmail = norm(workerEmail || "");
  const identityKey = cleanEmployeeId || (cleanWorkerEmail ? `email:${cleanWorkerEmail}` : "");
  if (!cleanBusinessId || !identityKey) {
    publishIosAgendaDebugV16D("profile_preflight_missing_identity_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null });
    return null;
  }

  const url = getWorkerCalendarProfileFunctionUrl(dbInstance);
  if (!url) {
    publishIosAgendaDebugV16D("profile_preflight_missing_url_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null });
    return null;
  }

  publishIosAgendaDebugV16D("profile_preflight_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null, url });
  const idToken = await getWorkerCalendarProfileIdToken(authObj, { allowManualTokenFallback: isIosCapacitor(), debugStage: "profile_token_check_v16g" });
  if (!idToken) {
    publishIosAgendaDebugV16D("profile_preflight_no_token_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null, url });
    return null;
  }

  const cacheKey = `${cleanBusinessId}:${identityKey}`;
  try {
    const cached = LOOP_WORKER_PROFILE_CACHE_V16.get(cacheKey);
    if (!fresh && cached && cached.profile && Date.now() - Number(cached.at || 0) < LOOP_WORKER_PROFILE_CACHE_TTL_MS_V16) {
      try {
        if (typeof window !== "undefined") {
          window.__LOOP_WORKER_PROFILE_CACHE_DEBUG__ = {
            stage: "cache_hit_v16g",
            businessId: cleanBusinessId,
            employeeId: cached.profile.employeeId || cleanEmployeeId || null,
            workerEmail: cleanWorkerEmail || null,
            ageMs: Date.now() - Number(cached.at || 0),
            at: new Date().toISOString(),
          };
        }
      } catch (_) {}
      return cached.profile;
    }
  } catch (_) {}

  if (cleanEmployeeId) {
    try {
      const localCached = readWorkerProfileLocalCacheV16B(cleanBusinessId, cleanEmployeeId);
      if (!fresh && localCached && localCached.profile) {
        LOOP_WORKER_PROFILE_CACHE_V16.set(cacheKey, { at: localCached.at, profile: localCached.profile });
        try {
          if (typeof window !== "undefined") {
            window.__LOOP_WORKER_PROFILE_CACHE_DEBUG__ = {
              stage: "local_cache_hit_v16b",
              businessId: cleanBusinessId,
              employeeId: cleanEmployeeId,
              ageMs: Date.now() - Number(localCached.at || 0),
              hasPhoto: Boolean(localCached.profile.photoUrl || localCached.profile.workerPhotoUrl || localCached.profile.employeePhotoUrl),
              hasAvailability: Boolean(localCached.profile.availability || localCached.profile.schedule || localCached.profile.workingHours || localCached.profile.hours),
              at: new Date().toISOString(),
            };
          }
        } catch (_) {}
        return localCached.profile;
      }
    } catch (_) {}
  }

  const payload = { businessId: cleanBusinessId, employeeId: cleanEmployeeId || "", workerEmail: cleanWorkerEmail || "" };
  try {
    const json = await workerCalendarPromiseWithTimeout(
      isIosCapacitor()
        ? httpRequestJson({
            url,
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
            data: payload,
          })
        : fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }).then(async (res) => {
            const body = await res.json().catch(() => null);
            if (!res.ok) return { ok: false, status: res.status, ...(body || {}) };
            return body;
          }),
      isIosCapacitor() ? 12000 : 10000,
      "worker_profile_function_timeout"
    );

    if (!json || json.ok !== true || !json.profile) {
      publishIosAgendaDebugV16D("profile_function_skipped_v16g", { status: json?.status || null, code: json?.code || json?.error || null, businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null });
      try { console.warn("[useAdminCalendarData] worker profile function skipped", { status: json?.status || null, code: json?.code || json?.error || null }); } catch (_) {}
      return null;
    }
    const resolvedEmployeeId = String(json.employeeId || json.profile?.employeeId || json.profile?.id || cleanEmployeeId || "").trim();
    const mergedProfile = mergeWorkerProfilesForCalendar(
      { id: resolvedEmployeeId || cleanEmployeeId || identityKey, employeeId: resolvedEmployeeId || cleanEmployeeId || identityKey, email: cleanWorkerEmail || "" },
      json.profile,
      { id: resolvedEmployeeId || json.profile?.id || cleanEmployeeId || identityKey, employeeId: resolvedEmployeeId || json.profile?.employeeId || cleanEmployeeId || identityKey }
    );
    try {
      LOOP_WORKER_PROFILE_CACHE_V16.set(cacheKey, { at: Date.now(), profile: mergedProfile });
      if (resolvedEmployeeId) {
        LOOP_WORKER_PROFILE_CACHE_V16.set(`${cleanBusinessId}:${resolvedEmployeeId}`, { at: Date.now(), profile: mergedProfile });
        writeWorkerProfileLocalCacheV16B(cleanBusinessId, resolvedEmployeeId, mergedProfile);
      }
      if (typeof window !== "undefined") {
        window.__LOOP_WORKER_PROFILE_CACHE_DEBUG__ = {
          stage: "cache_set_v16g",
          businessId: cleanBusinessId,
          employeeId: resolvedEmployeeId || cleanEmployeeId || null,
          workerEmail: cleanWorkerEmail || null,
          resolvedBy: json.resolvedBy || null,
          hasPhoto: Boolean(mergedProfile.photoUrl || mergedProfile.workerPhotoUrl || mergedProfile.employeePhotoUrl),
          hasAvailability: Boolean(mergedProfile.availability || mergedProfile.schedule || mergedProfile.workingHours || mergedProfile.hours),
          at: new Date().toISOString(),
        };
      }
      publishIosAgendaDebugV16D("profile_function_success_v16g", {
        businessId: cleanBusinessId,
        employeeId: resolvedEmployeeId || cleanEmployeeId || null,
        workerEmail: cleanWorkerEmail || null,
        resolvedBy: json.resolvedBy || null,
        hasPhoto: Boolean(mergedProfile.photoUrl || mergedProfile.workerPhotoUrl || mergedProfile.employeePhotoUrl),
        hasAvailability: Boolean(mergedProfile.availability || mergedProfile.schedule || mergedProfile.workingHours || mergedProfile.hours),
      });
    } catch (_) {}
    return mergedProfile;
  } catch (err) {
    publishIosAgendaDebugV16D("profile_function_failed_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null, error: err?.message || String(err), native: isIosCapacitor(), url });
    try { console.warn("[useAdminCalendarData] worker profile function failed", { employeeId: cleanEmployeeId, workerEmail: cleanWorkerEmail, error: err?.message || String(err), native: isIosCapacitor() }); } catch (_) {}
    return null;
  }
}


async function loadWorkerCalendarBookingsViaFunction({
  db: dbInstance,
  auth: authObj,
  businessId,
  employeeId,
  workerEmail = "",
  selectedEmployeeIds = [],
  start,
  end,
  selectedDate,
  viewMode,
  recordDebug = null,
}) {
  const debug = (stage, payload = {}) => {
    const next = publishIosAgendaDebugV16D(stage, payload);
    try {
      if (typeof recordDebug === "function") recordDebug(stage, payload);
    } catch (_) {}
    return next;
  };
  const cleanBusinessId = String(businessId || "").trim();
  const rawEmployeeId = String(employeeId || "").trim();
  const cleanEmployeeId = rawEmployeeId && !rawEmployeeId.startsWith("email:") ? rawEmployeeId : "";
  const cleanWorkerEmail = norm(workerEmail || "");
  if (!cleanBusinessId || (!cleanEmployeeId && !cleanWorkerEmail)) {
    debug("bookings_preflight_missing_identity_v16g", { businessId: cleanBusinessId, employeeId: cleanEmployeeId || null, workerEmail: cleanWorkerEmail || null });
    return null;
  }

  const url = getWorkerCalendarBookingsFunctionUrl(dbInstance);
  if (!url) {
    debug("bookings_preflight_missing_url_v16d", { businessId: cleanBusinessId, employeeId: cleanEmployeeId });
    return null;
  }

  debug("bookings_preflight_v16g", {
    businessId: cleanBusinessId,
    employeeId: cleanEmployeeId || null,
    workerEmail: cleanWorkerEmail || null,
    url,
    selectedEmployeeIds: Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds.map(String).filter(Boolean).slice(0, 20) : [],
  });

  const idToken = await getWorkerCalendarProfileIdToken(authObj, { allowManualTokenFallback: isIosCapacitor(), debugStage: "bookings_token_check_v16d" });
  if (!idToken) {
    debug("bookings_preflight_no_token_v16d", { businessId: cleanBusinessId, employeeId: cleanEmployeeId, url });
    return null;
  }

  const payload = {
    businessId: cleanBusinessId,
    employeeId: cleanEmployeeId || "",
    workerEmail: cleanWorkerEmail || "",
    selectedEmployeeIds: Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds.map(String).filter(Boolean).slice(0, 20) : [],
    startIso: start instanceof Date && !Number.isNaN(start.getTime()) ? start.toISOString() : "",
    endIso: end instanceof Date && !Number.isNaN(end.getTime()) ? end.toISOString() : "",
    selectedDateIso: selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime()) ? selectedDate.toISOString() : "",
    viewMode: String(viewMode || "day"),
  };

  try {
    debug("bookings_function_request_start_v16g", {
      businessId: cleanBusinessId,
      employeeId: cleanEmployeeId || null,
      workerEmail: cleanWorkerEmail || null,
      url,
      startIso: payload.startIso,
      endIso: payload.endIso,
      native: isIosCapacitor(),
    });
    const json = await workerCalendarPromiseWithTimeout(
      isIosCapacitor()
        ? httpRequestJson({
            url,
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
            data: payload,
          })
        : fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }).then(async (res) => {
            const body = await res.json().catch(() => null);
            if (!res.ok) return { ok: false, status: res.status, ...(body || {}) };
            return body;
          }),
      isIosCapacitor() ? 12000 : 10000,
      "worker_bookings_function_timeout"
    );

    if (!json || json.ok !== true || !Array.isArray(json.bookings)) {
      debug("bookings_function_skipped_v16g", {
        status: json?.status || null,
        code: json?.code || json?.error || null,
        businessId: cleanBusinessId,
        employeeId: cleanEmployeeId || null,
        workerEmail: cleanWorkerEmail || null,
        url,
      });
      return null;
    }

    const serverEmployeeId = String(json.employeeId || json.profile?.employeeId || json.profile?.id || cleanEmployeeId || "").trim();
    const serverWorkerEmail = norm(json.workerEmail || json.profile?.workerEmail || json.profile?.employeeEmail || json.profile?.email || cleanWorkerEmail || "");
    const serverWorkerName = String(json.profile?.name || json.profile?.displayName || json.profile?.employeeName || json.profile?.workerName || "").trim();

    const bookings = json.bookings.map((b) => {
      const original = b || {};
      const scoped = {
        ...original,
        originalEmployeeId: original.originalEmployeeId || original.employeeId || null,
        originalWorkerId: original.originalWorkerId || original.workerId || null,
        originalResourceId: original.originalResourceId || original.resourceId || null,
        employeeId: original.employeeId || original.workerId || original.resourceId || original.assignedEmployeeId || serverEmployeeId || null,
        workerId: original.workerId || original.employeeId || original.resourceId || original.assignedEmployeeId || serverEmployeeId || null,
        resourceId: original.resourceId || original.employeeId || original.workerId || original.assignedEmployeeId || serverEmployeeId || null,
        employeeEmail: original.employeeEmail || original.workerEmail || serverWorkerEmail || null,
        workerEmail: original.workerEmail || original.employeeEmail || serverWorkerEmail || null,
        employeeName: original.employeeName || original.workerName || serverWorkerName || null,
        workerName: original.workerName || original.employeeName || serverWorkerName || null,
        __workerCalendarFunctionScoped: true,
        __workerCalendarEmployeeId: serverEmployeeId || "",
        __workerCalendarWorkerEmail: serverWorkerEmail || "",
        __workerCalendarResolvedBy: json.resolvedBy || "",
        __workerCalendarSource: json.source || "getWorkerCalendarBookingsHttp",
      };
      return normalizeBookingRecord(scoped);
    }).filter(Boolean);

    debug("bookings_function_success_v16j", {
      businessId: cleanBusinessId,
      employeeId: serverEmployeeId || cleanEmployeeId || null,
      workerEmail: serverWorkerEmail || cleanWorkerEmail || null,
      resolvedBy: json.resolvedBy || null,
      rawCount: Number(json.rawCount || bookings.length || 0),
      filteredCount: bookings.length,
      serverFilteredCount: Number(json.filteredCount || bookings.length || 0),
      source: json.source || "getWorkerCalendarBookingsHttp",
      startIso: payload.startIso,
      endIso: payload.endIso,
      samples: bookings.slice(0, 8).map((b) => ({
        id: b.id || b.bookingId || null,
        status: b.status || null,
        employeeId: b.employeeId || b.workerId || b.resourceId || b.assignedEmployeeId || null,
        employeeEmail: b.employeeEmail || b.workerEmail || b.assignedEmployeeEmail || null,
        scheduledAt: b.scheduledAt || b.scheduledAtIso || b.scheduledAtDate || b.date || null,
        functionScoped: b.__workerCalendarFunctionScoped === true,
      })),
    });
    return bookings;
  } catch (err) {
    debug("bookings_function_failed_v16g", {
      businessId: cleanBusinessId,
      employeeId: cleanEmployeeId || null,
      workerEmail: cleanWorkerEmail || null,
      error: err?.message || String(err),
      native: isIosCapacitor(),
      url,
    });
    return null;
  }
}

function isOwnerOrAdminForBusiness(businessDoc, actor) {
  if (!businessDoc || !actor) return false;
  const uid = actor.uid ? String(actor.uid) : null;
  const email = actor.email ? String(actor.email).toLowerCase() : "";

  if (businessDoc.ownerUid && uid && String(businessDoc.ownerUid) === uid) return true;
  if (businessDoc.ownerEmail && email && String(businessDoc.ownerEmail).toLowerCase() === email) return true;
  if (businessDoc.admins && uid && uid in businessDoc.admins) return true;
  if (businessDoc.managers && uid && uid in businessDoc.managers) return true;
  return false;
}

function employeeIdentityForCalendar(emp = {}) {
  const ids = [
    emp.id,
    emp.employeeId,
    emp.workerId,
    emp.userUid,
    emp.userId,
    emp.uid,
    emp.authUid,
    emp.linkedUid,
    emp.linkedUserId,
    emp.employeeUserUid,
    emp.employeeUid,
    emp.workerUid,
    emp.workerUserUid,
    emp.firebaseUid,
  ].filter(Boolean).map(String);

  const emails = [
    emp.email,
    emp.emailLower,
    emp.emailNormalized,
    emp.employeeEmail,
    emp.employeeEmailLower,
    emp.employeeEmailNormalized,
    emp.workerEmail,
    emp.workerEmailLower,
    emp.workerEmailNormalized,
    emp.inviteEmail,
    emp.inviteEmailLower,
    emp.inviteEmailNormalized,
    emp.contactEmail,
    emp.contactEmailLower,
    emp.contactEmailNormalized,
    emp.userEmail,
    emp.userEmailLower,
    emp.loginEmail,
    emp.loginEmailLower,
    ...(Array.isArray(emp.accessEmails) ? emp.accessEmails : []),
    ...(Array.isArray(emp.allowedEmails) ? emp.allowedEmails : []),
  ].filter(Boolean).map(norm);

  const names = [
    emp.name,
    emp.displayName,
    emp.employeeName,
    emp.fullName,
  ].filter(Boolean).map(norm);

  // Phone/WhatsApp fields are intentionally NOT part of access identity.
  // They are contact/delivery fields only; worker access is resolved by UID first,
  // then email as a compatibility fallback.
  return { ids: Array.from(new Set(ids)), emails: Array.from(new Set(emails)), names: Array.from(new Set(names)) };
}

function findMatchingEmployee(employees, actor) {
  const uid = actor && actor.uid ? String(actor.uid) : null;
  const email = actor && actor.email ? norm(actor.email) : "";
  for (const e of employees || []) {
    const ident = employeeIdentityForCalendar(e);
    if ((uid && ident.ids.includes(uid)) || (email && ident.emails.includes(email))) {
      return e;
    }
  }

  return null;
}

async function loadSelfEmployeeRowsForBusiness(db, businessId, actor = {}, routeEmail = null, routeId = null) {
  if (!db || !businessId) return [];
  // LOOP WORKER AGENDA IOS FIRST PAINT V10C
  // This helper uses Firestore Web SDK queries. In iOS Capacitor we already have
  // the worker's own profile through workerAccess/getWorkerCalendarProfileHttp;
  // avoid these Web SDK queries because they can hang in WKWebView and keep the
  // first-paint lock stuck on "Cargando tu agenda".
  if (isIosCapacitor()) return [];
  const uid = actor?.uid ? String(actor.uid) : "";
  const email = norm(routeEmail || actor?.email || "");
  const rows = [];
  const seen = new Set();

  const pushSnap = (snap) => {
    try {
      snap.docs.forEach((d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        rows.push({ id: d.id, ...(d.data() || {}) });
      });
    } catch (_) {}
  };

  const employeesCol = collection(db, `businesses/${businessId}/employees`);
  const queries = [];

  if (routeId) {
    try {
      const one = await getDoc(doc(db, `businesses/${businessId}/employees`, String(routeId)));
      if (one.exists()) {
        rows.push({ id: one.id, ...(one.data() || {}) });
        seen.add(one.id);
      }
    } catch (_) {}
  }

  if (uid) {
    ["userUid", "uid", "userId", "authUid", "linkedUserId", "linkedUserUid", "workerUid", "workerUserUid", "employeeUid", "employeeUserUid"].forEach((field) => {
      queries.push(getDocs(query(employeesCol, where(field, "==", uid))));
    });
  }

  if (email) {
    [
      "email", "emailLower", "emailNormalized",
      "workerEmail", "workerEmailLower", "workerEmailNormalized",
      "employeeEmail", "employeeEmailLower", "employeeEmailNormalized",
      "inviteEmail", "inviteEmailLower", "inviteEmailNormalized",
      "contactEmail", "contactEmailLower", "contactEmailNormalized",
      "userEmail", "userEmailLower", "loginEmail", "loginEmailLower",
    ].forEach((field) => {
      queries.push(getDocs(query(employeesCol, where(field, "==", email))));
    });
    queries.push(getDocs(query(employeesCol, where("accessEmails", "array-contains", email))));
    queries.push(getDocs(query(employeesCol, where("allowedEmails", "array-contains", email))));
  }


  const settled = await Promise.allSettled(queries);
  settled.forEach((res) => {
    if (res.status === "fulfilled") pushSnap(res.value);
  });

  return rows;
}

function workerPhotoFromRouteStateValue(value) {
  if (!value) return "";
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value && typeof value === "object") {
    const nested = value.url || value.downloadURL || value.src || value.href || "";
    if (typeof nested === "string" && nested.trim()) return nested.trim();
  }
  return "";
}

function workerProfileFromRouteState(routeState = null) {
  const state = routeState && typeof routeState === "object" ? routeState : null;
  if (!state) return null;
  const raw = state.raw && typeof state.raw === "object" ? state.raw : {};
  const employee = state.employee && typeof state.employee === "object" ? state.employee : {};
  const worker = state.worker && typeof state.worker === "object" ? state.worker : {};
  const item = state.item && typeof state.item === "object" ? state.item : {};
  const source = { ...raw, ...item, ...employee, ...worker, ...state };
  const id = String(source.employeeId || source.workerId || source.resourceId || source.id || source.employee?.id || source.worker?.id || "").trim();
  const email = norm(source.employeeEmail || source.workerEmail || source.email || source.employee?.email || source.worker?.email || "");
  const name = String(source.workerName || source.employeeName || source.workerDisplayName || source.employeeDisplayName || source.displayName || source.name || source.fullName || source.employee?.name || source.worker?.name || "").trim();
  const photoCandidates = [
    source.photoUrl, source.photoURL, source.photo, source.imageUrl, source.image, source.avatarUrl, source.avatarURL,
    source.profileImageUrl, source.profilePhotoUrl, source.pictureUrl, source.workerPhotoUrl, source.employeePhotoUrl,
    source.employee?.photoUrl, source.worker?.photoUrl, source.employee?.imageUrl, source.worker?.imageUrl,
  ];
  let photoUrl = "";
  for (const candidate of photoCandidates) {
    photoUrl = workerPhotoFromRouteStateValue(candidate);
    if (photoUrl) break;
  }
  const availability = source.availability || source.employeeAvailability || source.workerAvailability || source.schedule || source.workingHours || source.hours || source.employee?.availability || source.worker?.availability || null;
  if (!id && !email && !name && !photoUrl && !availability) return null;
  return {
    ...source,
    id: id || (email ? `email:${email}` : ""),
    employeeId: id || (email ? `email:${email}` : ""),
    workerId: id || "",
    email,
    emailLower: email,
    employeeEmail: source.employeeEmail || email || "",
    employeeEmailLower: email,
    workerEmail: source.workerEmail || email || "",
    workerEmailLower: email,
    name,
    displayName: name,
    photoUrl,
    photo: photoUrl,
    image: photoUrl,
    avatarUrl: photoUrl,
    availability,
    schedule: source.schedule || null,
    workingHours: source.workingHours || null,
    hours: source.hours || null,
  };
}


function hasWorkerProfileSignal(profile = null) {
  if (!profile || typeof profile !== "object") return false;
  return Boolean(
    profile.id ||
    profile.employeeId ||
    profile.workerId ||
    profile.email ||
    profile.employeeEmail ||
    profile.workerEmail ||
    profile.name ||
    profile.displayName ||
    profile.employeeName ||
    profile.workerName ||
    profile.photoUrl ||
    profile.workerPhotoUrl ||
    profile.employeePhotoUrl ||
    profile.availability ||
    profile.schedule ||
    profile.workingHours ||
    profile.hours ||
    profile.permissions ||
    profile.workerPermissions
  );
}

function isMeaningfulWorkerValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const clean = value.trim();
    return Boolean(clean && clean !== "Sin nombre" && clean !== "Mi agenda" && clean !== "worker" && clean !== "undefined" && clean !== "null");
  }
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function mergeWorkerProfilesForCalendar(...profiles) {
  const out = {};
  profiles.filter(Boolean).forEach((profile) => {
    Object.entries(profile || {}).forEach(([key, value]) => {
      if (isMeaningfulWorkerValue(value)) out[key] = value;
    });
  });

  const email = norm(out.email || out.emailLower || out.employeeEmail || out.employeeEmailLower || out.workerEmail || out.workerEmailLower || out.inviteEmail || out.contactEmail || "");
  const id = String(out.id || out.employeeId || out.workerId || out.resourceId || "").trim();
  const name = String(out.name || out.displayName || out.employeeName || out.workerName || out.fullName || "").trim();
  const photoUrl = workerPhotoFromRouteStateValue(out.photoUrl || out.photoURL || out.photo || out.imageUrl || out.image || out.avatarUrl || out.workerPhotoUrl || out.employeePhotoUrl || "");
  const availability = out.availability || out.employeeAvailability || out.workerAvailability || out.schedule || out.workingHours || out.hours || null;

  if (!id && !email && !name && !photoUrl && !availability && !out.permissions && !out.workerPermissions) return null;

  return {
    ...out,
    id: id || (email ? `email:${email}` : ""),
    employeeId: id || (email ? `email:${email}` : ""),
    workerId: out.workerId || id || "",
    email,
    emailLower: email,
    employeeEmail: out.employeeEmail || email || "",
    employeeEmailLower: email,
    workerEmail: out.workerEmail || email || "",
    workerEmailLower: email,
    name,
    displayName: name,
    employeeName: out.employeeName || name,
    workerName: out.workerName || name,
    photoUrl,
    photo: photoUrl,
    image: photoUrl,
    avatarUrl: photoUrl,
    availability,
    schedule: out.schedule || null,
    workingHours: out.workingHours || null,
    hours: out.hours || null,
  };
}

function workerAccessProfileFromMirrorDoc(data = {}, fallback = {}) {
  if (!data || typeof data !== "object") return null;
  const email = norm(data.email || data.employeeEmail || data.workerEmail || fallback.email || fallback.employeeEmail || fallback.workerEmail || "");
  const id = String(data.employeeId || data.workerId || data.resourceId || fallback.employeeId || fallback.workerId || fallback.id || "").trim();
  const name = String(data.name || data.displayName || data.employeeName || data.workerName || fallback.name || fallback.displayName || fallback.employeeName || fallback.workerName || "").trim();
  const photoUrl = workerPhotoFromRouteStateValue(data.photoUrl || data.photoURL || data.photo || data.workerPhotoUrl || data.employeePhotoUrl || data.imageUrl || data.image || data.avatarUrl || fallback.photoUrl || fallback.workerPhotoUrl || fallback.employeePhotoUrl || "");
  const availability = data.availability || data.employeeAvailability || data.workerAvailability || data.schedule || data.workingHours || data.hours || fallback.availability || fallback.schedule || fallback.workingHours || fallback.hours || null;
  return mergeWorkerProfilesForCalendar({
    ...fallback,
    ...data,
    id: id || fallback.id || "",
    employeeId: id || fallback.employeeId || "",
    workerId: id || fallback.workerId || "",
    email,
    emailLower: email,
    employeeEmail: data.employeeEmail || email || fallback.employeeEmail || "",
    employeeEmailLower: email,
    workerEmail: data.workerEmail || email || fallback.workerEmail || "",
    workerEmailLower: email,
    name,
    displayName: name,
    employeeName: name,
    workerName: name,
    photoUrl,
    photo: photoUrl,
    image: photoUrl,
    avatarUrl: photoUrl,
    workerPhotoUrl: photoUrl,
    employeePhotoUrl: photoUrl,
    availability,
    employeeAvailability: data.employeeAvailability || availability,
    workerAvailability: data.workerAvailability || availability,
    schedule: data.schedule || availability,
    workingHours: data.workingHours || availability,
    hours: data.hours || null,
    services: Array.isArray(data.services) ? data.services : (Array.isArray(fallback.services) ? fallback.services : []),
    serviceIds: Array.isArray(data.serviceIds) ? data.serviceIds : (Array.isArray(fallback.serviceIds) ? fallback.serviceIds : []),
    blockedDates: Array.isArray(data.blockedDates) ? data.blockedDates : (Array.isArray(fallback.blockedDates) ? fallback.blockedDates : []),
    blockedRanges: Array.isArray(data.blockedRanges) ? data.blockedRanges : (Array.isArray(fallback.blockedRanges) ? fallback.blockedRanges : []),
    permissions: data.permissions || data.workerPermissions || fallback.permissions || fallback.workerPermissions || null,
    workerPermissions: data.workerPermissions || data.permissions || fallback.workerPermissions || fallback.permissions || null,
  });
}

function normalizeCalendarServiceRow(service = {}, fallbackId = "") {
  if (!service || typeof service !== "object") return null;
  const id = String(service.id || service.serviceId || service.serviceID || service.key || service.value || fallbackId || "").trim();
  const name = String(service.name || service.title || service.label || service.serviceName || "").trim();
  if (!id && !name) return null;
  return {
    ...service,
    id: id || name,
    serviceId: id || service.serviceId || name,
    name: name || service.name || service.title || "Servicio",
    title: service.title || name || service.name || "Servicio",
  };
}

function workerVisibleServicesFromProfiles(...profiles) {
  const byId = new Map();
  const add = (service, fallbackId = "") => {
    const normalized = normalizeCalendarServiceRow(service, fallbackId);
    if (!normalized) return;
    if (normalized.disabled === true || normalized.active === false || normalized.visible === false) return;
    const id = String(normalized.id || normalized.serviceId || fallbackId || "").trim();
    if (!id) return;
    if (!byId.has(id)) byId.set(id, normalized);
  };

  (profiles || []).forEach((profile) => {
    if (!profile || typeof profile !== "object") return;
    [
      profile.services,
      profile.workerVisibleServices,
      profile.visibleServices,
      profile.assignedServices,
      profile.allowedServices,
      profile.serviceOptions,
    ].forEach((list) => {
      if (Array.isArray(list)) list.forEach((row, idx) => add(row, row?.id || row?.serviceId || `service_${idx}`));
    });

    [profile.serviceMap, profile.servicesMap, profile.serviceAssignments].forEach((map) => {
      if (map && typeof map === "object" && !Array.isArray(map)) {
        Object.entries(map).forEach(([key, value]) => {
          if (value && typeof value === "object") add({ id: key, serviceId: key, ...value }, key);
        });
      }
    });
  });

  return Array.from(byId.values());
}

function workerProfileHasCalendarDetails(profile = {}) {
  if (!profile || typeof profile !== "object") return false;
  const name = String(profile.name || profile.displayName || profile.employeeName || profile.workerName || "").trim();
  const email = norm(profile.email || profile.employeeEmail || profile.workerEmail || "");
  const genericName = !name || (email && name.toLowerCase() === email.split("@")[0]);
  return Boolean(
    (!genericName && name) ||
    profile.photoUrl || profile.photo || profile.image || profile.avatarUrl || profile.workerPhotoUrl || profile.employeePhotoUrl ||
    profile.availability || profile.employeeAvailability || profile.workerAvailability || profile.schedule || profile.workingHours || profile.hours ||
    workerVisibleServicesFromProfiles(profile).length > 0
  );
}

async function loadWorkerAccessProfileForBusiness(db, businessId, actor = {}, routeEmail = null, routeId = null, routeProfile = null, authObj = null) {
  if (!db || !businessId) return null;
  const uid = actor?.uid ? String(actor.uid) : "";
  const email = norm(routeEmail || actor?.email || routeProfile?.email || routeProfile?.employeeEmail || routeProfile?.workerEmail || "");
  const baseFallback = mergeWorkerProfilesForCalendar(routeProfile || {}, { id: routeId || "", employeeId: routeId || "", email }) || { id: routeId || "", employeeId: routeId || "", email };
  const docs = [];
  const seenEmployeeDocs = new Set();
  const seenFunctionProfiles = new Set();

  const pushFunctionProfile = async (employeeId, workerEmailOverride = email) => {
    const rawId = String(employeeId || "").trim();
    const cleanId = rawId && !rawId.startsWith("email:") ? rawId : "";
    const cleanEmail = norm(workerEmailOverride || email || "");
    const key = cleanId || (cleanEmail ? `email:${cleanEmail}` : "");
    if (!key || seenFunctionProfiles.has(key)) return;
    seenFunctionProfiles.add(key);
    const profile = await loadWorkerCalendarProfileViaFunction({ db, auth: authObj, businessId, employeeId: cleanId, workerEmail: cleanEmail });
    if (profile && typeof profile === "object") {
      const resolvedId = String(profile.id || profile.employeeId || cleanId || key || "").trim();
      docs.push(mergeWorkerProfilesForCalendar(baseFallback, { id: resolvedId, employeeId: resolvedId, email: cleanEmail }, profile));
      try { console.info("[useAdminCalendarData] loaded worker profile via function", { employeeId: resolvedId || cleanId || null, workerEmail: cleanEmail || null, hasName: Boolean(profile.name || profile.employeeName || profile.workerName), hasPhoto: Boolean(profile.photoUrl || profile.workerPhotoUrl || profile.employeePhotoUrl), hasAvailability: Boolean(profile.availability || profile.schedule || profile.workingHours) }); } catch (_) {}
    }
  };

  const pushAccessDoc = async (path) => {
    try {
      let data = null;
      if (isIosCapacitor() && typeof fsClient.getDocByPath === "function") {
        data = await fsClient.getDocByPath(path, { db });
      } else {
        const snap = await getDoc(doc(db, path));
        data = snap && snap.exists() ? snap.data() : null;
      }
      if (data && typeof data === "object") docs.push(workerAccessProfileFromMirrorDoc(data, baseFallback));
    } catch (_) {}
  };

  const pushEmployeeDoc = async (employeeId) => {
    const cleanId = String(employeeId || "").trim();
    if (!cleanId || cleanId.startsWith("email:") || seenEmployeeDocs.has(cleanId)) return;
    seenEmployeeDocs.add(cleanId);
    try {
      let data = null;
      if (isIosCapacitor() && typeof fsClient.getDocByPath === "function") {
        data = await fsClient.getDocByPath(`businesses/${businessId}/employees/${cleanId}`, { db });
      } else {
        const snap = await getDoc(doc(db, `businesses/${businessId}/employees`, cleanId));
        data = snap && snap.exists() ? snap.data() : null;
      }
      if (data && typeof data === "object") {
        docs.push(mergeWorkerProfilesForCalendar(baseFallback, { id: cleanId, employeeId: cleanId }, data));
      }
    } catch (err) {
      try { console.warn("[useAdminCalendarData] employee doc enrichment skipped", { employeeId: cleanId, error: err?.message || String(err) }); } catch (_) {}
      await pushFunctionProfile(cleanId);
    }
  };

  // LOOP WORKER AGENDA INITIAL LOAD V10
  // Function-first avoids a guaranteed Firestore rules denial on worker employee docs.
  // If the function returns a rich profile, skip the direct employee doc enrichment.
  if (routeId) await pushFunctionProfile(routeId, email);
  if (!routeId && email) await pushFunctionProfile("", email);
  if (uid) await pushAccessDoc(`businesses/${businessId}/workerAccess/${uid}`);
  if (email) await pushAccessDoc(`businesses/${businessId}/workerAccessEmails/${email}`);

  let merged = mergeWorkerProfilesForCalendar(baseFallback, ...docs.filter(Boolean));
  const mergedId = String(merged?.id || merged?.employeeId || merged?.workerId || routeId || "").trim();
  const needsEmployeeDoc = Boolean(
    mergedId &&
    !mergedId.startsWith("email:") &&
    !workerProfileHasCalendarDetails(merged)
  );
  if (needsEmployeeDoc) {
    await pushEmployeeDoc(mergedId);
    merged = mergeWorkerProfilesForCalendar(baseFallback, ...docs.filter(Boolean));
  }

  const stillNeedsProfile = Boolean(
    mergedId &&
    !mergedId.startsWith("email:") &&
    !workerProfileHasCalendarDetails(merged)
  );
  if (stillNeedsProfile) {
    await pushFunctionProfile(mergedId);
    merged = mergeWorkerProfilesForCalendar(baseFallback, ...docs.filter(Boolean));
  }

  return hasWorkerProfileSignal(merged) ? merged : null;
}

function bookingMatchesCurrentWorker(bookingLike, actor, detectedEmployeeDocId, workerEmailMode, currentWorkerEmployee, selectedEmployeeIds = []) {
  const booking = bookingLike || {};
  // LOOP AGENDA IOS SERVER-SCOPED BOOKINGS RENDER V16J:
  // These bookings already passed the authenticated Cloud Function worker filter.
  // Do not drop them again because selectedEmployeeIds may still contain email:... during iOS hydration.
  if (booking.__workerCalendarFunctionScoped === true) {
    return true;
  }
  const employeeIdentity = employeeIdentityForCalendar(currentWorkerEmployee || {});

  const workerIds = Array.from(new Set([
    detectedEmployeeDocId,
    ...(Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds : []),
    actor?.uid,
    ...employeeIdentity.ids,
  ].filter(Boolean).map(String)));

  const workerEmails = Array.from(new Set([
    workerEmailMode,
    actor?.email,
    ...employeeIdentity.emails,
  ].filter(Boolean).map(norm)));

  const bookingIds = [
    booking.employeeId,
    booking.workerId,
    booking.resourceId,
    booking.assignedEmployeeId,
    booking.employeeUserUid,
    booking.employeeUserId,
    booking.assignedEmployeeUserUid,
    booking.assignedEmployeeUserId,
    ...(Array.isArray(booking.assignedEmployeeIds) ? booking.assignedEmployeeIds : []),
  ].filter(Boolean).map(String);

  const bookingEmails = [
    booking.employeeEmail,
    booking.employeeEmailLower,
    booking.workerEmail,
    booking.workerEmailLower,
    booking.assignedEmployeeEmail,
    booking.employeeAuthEmail,
    booking.authEmail,
    booking.userEmail,
  ].filter(Boolean).map(norm);

  if (workerIds.length && bookingIds.some((id) => workerIds.includes(id))) return true;
  if (workerEmails.length && bookingEmails.some((email) => workerEmails.includes(email))) return true;

  const assignedEmployees = Array.isArray(booking.assignedEmployees) ? booking.assignedEmployees : [];
  return assignedEmployees.some((emp) => {
    const ident = employeeIdentityForCalendar(emp);
    return Boolean(
      ident.ids.some((id) => workerIds.includes(id)) ||
      ident.emails.some((email) => workerEmails.includes(email))
    );
  });
}


function bookingMatchesWorkerEmployeeIdOnly(bookingLike, employeeId) {
  const workerId = String(employeeId || "").trim();
  if (!workerId) return false;

  const booking = bookingLike || {};
  const bookingIds = [
    booking.employeeId,
    booking.workerId,
    booking.resourceId,
    booking.assignedEmployeeId,
    ...(Array.isArray(booking.assignedEmployeeIds) ? booking.assignedEmployeeIds : []),
  ].filter(Boolean).map((value) => String(value).trim());

  if (bookingIds.includes(workerId)) return true;

  const assignedEmployees = Array.isArray(booking.assignedEmployees) ? booking.assignedEmployees : [];
  return assignedEmployees.some((emp) => {
    const id = String(emp?.id || emp?.employeeId || emp?.workerId || emp?.resourceId || "").trim();
    return Boolean(id && id === workerId);
  });
}

function bookingDateForFilter(b) {
  try {
    return (
      bookingScheduledAtToDate(b) ||
      (b && b.scheduledAt && typeof b.scheduledAt.toDate === "function" ? b.scheduledAt.toDate() : null) ||
      (b && b.scheduledAt ? new Date(b.scheduledAt) : null)
    );
  } catch (_) {
    return null;
  }
}

function calendarMonthStartForAgenda(date) {
  const base = date ? new Date(date) : new Date();
  return startOfDay(new Date(base.getFullYear(), base.getMonth(), 1));
}

function calendarMonthEndExclusiveForAgenda(date) {
  const base = date ? new Date(date) : new Date();
  return startOfDay(new Date(base.getFullYear(), base.getMonth() + 1, 1));
}

function normalizeBookingRecord(b) {
  const scheduledAtDate = bookingDateForFilter(b);
  return {
    ...(b || {}),
    id: b?.id || b?.bookingId || null,
    scheduledAtDate,
  };
}

function workerBookingDirectIdCandidatesV16F(b = {}) {
  const values = [
    b.employeeId,
    b.workerId,
    b.resourceId,
    b.assignedEmployeeId,
    b.employee?.id,
    b.employee?.employeeId,
    b.worker?.id,
    b.worker?.employeeId,
    ...(Array.isArray(b.assignedEmployeeIds) ? b.assignedEmployeeIds : []),
    ...(Array.isArray(b.assignedEmployees)
      ? b.assignedEmployees.flatMap((emp) => [emp?.id, emp?.employeeId, emp?.workerId, emp?.resourceId, emp?.uid, emp?.userId])
      : []),
  ];
  return Array.from(new Set(values.map((value) => String(value || "").trim()).filter((value) => value && value !== "__unassigned__" && !value.startsWith("email:"))));
}

function bookingWorkerNameCandidateV16F(b = {}) {
  return String(
    b.employeeName ||
    b.workerName ||
    b.assignedEmployeeName ||
    b.resourceName ||
    b.employee?.name ||
    b.worker?.name ||
    b.employee?.displayName ||
    b.worker?.displayName ||
    ""
  ).trim();
}

function bookingHasWorkerEmailV16F(b = {}, wantedEmail = "") {
  const wanted = norm(wantedEmail);
  if (!wanted) return false;
  const values = [
    b.employeeEmail,
    b.employeeEmailLower,
    b.workerEmail,
    b.workerEmailLower,
    b.assignedEmployeeEmail,
    b.assignedEmployeeEmailLower,
    b.createdByWorkerEmail,
    b.createdByWorkerEmailLower,
    b.employee?.email,
    b.worker?.email,
  ];
  return values.map(norm).filter(Boolean).includes(wanted);
}

function deriveWorkerIdentityFromBookingsV16F(bookings = [], workerEmail = "", options = {}) {
  const wantedEmail = norm(workerEmail);
  const requireEmailMatch = options.requireEmailMatch === true;
  const scored = new Map();

  (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
    const b = normalizeBookingRecord(booking || {});
    const bookingEmail = bookingWorkerEmailForCalendarResource(b);
    const emailMatches = Boolean(wantedEmail && (bookingEmail === wantedEmail || bookingHasWorkerEmailV16F(b, wantedEmail)));
    if (requireEmailMatch && !emailMatches) return;

    const ids = workerBookingDirectIdCandidatesV16F(b);
    if (!ids.length) return;

    const name = bookingWorkerNameCandidateV16F(b);
    ids.forEach((id) => {
      const previous = scored.get(id) || { id, score: 0, count: 0, name: "", email: "" };
      previous.count += 1;
      previous.score += 1;
      if (emailMatches) previous.score += 100;
      if (name && !previous.name) {
        previous.name = name;
        previous.score += 8;
      }
      if (bookingEmail && !previous.email) previous.email = bookingEmail;
      scored.set(id, previous);
    });
  });

  const best = Array.from(scored.values()).sort((a, b) => b.score - a.score || b.count - a.count)[0] || null;
  if (!best || !best.id) return null;

  const email = wantedEmail || norm(best.email || "");
  const fallbackName = best.name || (email ? email.split("@")[0] : "Trabajador");
  return {
    id: best.id,
    employeeId: best.id,
    workerId: best.id,
    resourceId: best.id,
    name: fallbackName,
    displayName: fallbackName,
    employeeName: fallbackName,
    workerName: fallbackName,
    email,
    employeeEmail: email,
    workerEmail: email,
    emailLower: email,
    employeeEmailLower: email,
    workerEmailLower: email,
    __derivedFromBookingsV16F: true,
    __bookingIdentityScore: best.score,
    __bookingIdentityCount: best.count,
  };
}

function applyBookingsFilter(rawBookings, {
  selectedDate,
  viewMode,
  isWorkerView,
  detectedEmployeeDocId,
  workerEmailMode,
  selectedEmployeeIds,
  businessDoc,
  actor,
  currentWorkerPermissions,
  currentWorkerEmployee,
}) {
  let start;
  let end;

  if (viewMode === "month") {
    start = calendarMonthStartForAgenda(selectedDate);
    end = calendarMonthEndExclusiveForAgenda(selectedDate);
  } else if (viewMode === "weekCompany") {
    start = startOfWeek(selectedDate);
    end = endOfWeekExclusiveFromStart(start);
  } else if (isWorkerView) {
    start = startOfWeek(selectedDate);
    end = endOfWeekExclusiveFromStart(start);
  } else {
    start = startOfDay(selectedDate);
    end = endOfDayExclusive(selectedDate);
  }

  const actorIsOwnerOrAdmin = isOwnerOrAdminForBusiness(businessDoc, actor);
  const isNoWorkers = false;
  const selectedSet = new Set((selectedEmployeeIds || []).map(String));
  const actorEmail = actor && actor.email ? actor.email : "";
  const workerPerms = normalizeWorkerPermissions(currentWorkerPermissions);

  return (rawBookings || [])
    .map(normalizeBookingRecord)
    .filter((b) => {
      const d = b && b.scheduledAtDate ? new Date(b.scheduledAtDate) : null;
      if (!d || isNaN(d.getTime())) return false;
      return d >= start && d < end;
    })
    .filter((b) => {
      // LOOP AGENDA MADRE PHASE A V67:
      // Worker view defaults to own agenda. If the worker has canViewAllCalendars
      // and actively selects team resources, allow those selected resource ids.
      if (isWorkerView) {
        if (!workerPerms.canViewOwnCalendar && !workerPerms.canViewAllCalendars) return false;

        if (workerPerms.canViewAllCalendars && selectedSet.size > 0) {
          const bookingResourceIds = [
            b.employeeId,
            b.workerId,
            b.resourceId,
            b.assignedEmployeeId,
            ...(Array.isArray(b.assignedEmployeeIds) ? b.assignedEmployeeIds : []),
          ].filter(Boolean).map(String);

          if (bookingResourceIds.some((id) => selectedSet.has(id))) return true;
        }

        return bookingMatchesCurrentWorker(
          b,
          actor,
          detectedEmployeeDocId,
          workerEmailMode,
          currentWorkerEmployee,
          selectedEmployeeIds
        );
      }

      if (viewMode === "weekCompany") return true;

      if (!actorIsOwnerOrAdmin && !isNoWorkers) {
        if (workerEmailMode) return norm(b.employeeEmail) === norm(actorEmail);
        if (selectedSet.size > 0) return selectedSet.has(String(b.employeeId || ""));
      }

      return true;
    })
    .sort((a, b) => {
      const da = a && a.scheduledAtDate ? new Date(a.scheduledAtDate).getTime() : 0;
      const dbv = b && b.scheduledAtDate ? new Date(b.scheduledAtDate).getTime() : 0;
      return da - dbv;
    });
}



function bookingWorkerEmailForCalendarResource(b = {}) {
  return norm(
    b.employeeEmailLower ||
    b.employeeEmail ||
    b.workerEmailLower ||
    b.workerEmail ||
    b.assignedEmployeeEmailLower ||
    b.assignedEmployeeEmail ||
    b.createdByWorkerEmail ||
    ""
  );
}

function employeeMatchesCalendarEmail(emp = {}, email = "") {
  const wanted = norm(email);
  if (!wanted) return false;
  return employeeIdentityForCalendar(emp).emails.includes(wanted);
}

function resolveBookingResourceIdForCalendar(b = {}, employees = []) {
  const scopedResourceId = String(b.__workerCalendarEmployeeId || "").trim();
  if (scopedResourceId) return scopedResourceId;

  const direct = b.employeeId || b.workerId || b.resourceId || b.assignedEmployeeId;
  if (direct) return String(direct);

  const assignedIds = Array.isArray(b.assignedEmployeeIds) ? b.assignedEmployeeIds.filter(Boolean).map(String) : [];
  if (assignedIds.length > 0) return assignedIds[0];

  const assignedEmployees = Array.isArray(b.assignedEmployees) ? b.assignedEmployees : [];
  for (const emp of assignedEmployees) {
    const id = emp?.id || emp?.employeeId || emp?.workerId || emp?.uid || emp?.userId || emp?.userUid;
    if (id) return String(id);
  }

  const email = bookingWorkerEmailForCalendarResource(b);
  if (email) {
    const match = (Array.isArray(employees) ? employees : []).find((emp) => employeeMatchesCalendarEmail(emp, email));
    const matchedId = match?.id || match?.employeeId || match?.workerId || match?.uid || match?.userUid || match?.userId;
    if (matchedId) return String(matchedId);

    // Last-resort resource so owner/admin resource calendars can still render email-only bookings.
    return `email:${email}`;
  }

  return "";
}

function isCalendarUnassignedBooking(b) {
  const assignmentStatus = String(b?.assignmentStatus || "").toLowerCase();
  const requestedMode = String(b?.requestedWorkerMode || "").toLowerCase();
  const label = String(b?.employeeName || b?.workerName || "").toLowerCase();
  if (assignmentStatus === "unassigned") return true;
  if (requestedMode === "any") return true;
  if (label.includes("cualquiera")) return true;
  return !(b?.employeeId || b?.workerId || b?.resourceId);
}

export function useAdminCalendarData({ db, auth, businessIdProp, selectedDate, viewMode, routeState = null }) {
  const [activeBusinessId, setActiveBusinessId] = useState(businessIdProp || null);
  useEffect(() => {
    setActiveBusinessId(businessIdProp || null);
  }, [businessIdProp]);

  const [businessDoc, setBusinessDoc] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoadError, setBookingsLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [workerAccessError, setWorkerAccessError] = useState('');

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [isWorkerView, setIsWorkerView] = useState(false);
  const [workerEmailMode, setWorkerEmailMode] = useState(null);
  const [detectedEmployeeDocId, setDetectedEmployeeDocId] = useState(null);
  const [currentWorkerEmployee, setCurrentWorkerEmployee] = useState(null);
  const [currentWorkerPermissions, setCurrentWorkerPermissions] = useState(DEFAULT_WORKER_PERMISSIONS);

  const [employeeHeaderColors, setEmployeeHeaderColors] = useState({});

  const [businessPendingCount, setBusinessPendingCount] = useState(0);
  const [businessToConfirmCount, setBusinessToConfirmCount] = useState(0);
  const [workerPendingCount, setWorkerPendingCount] = useState(0);
  const [workerToConfirmCount, setWorkerToConfirmCount] = useState(0);
  const pendingCountUnsubRef = useRef(null);
  const confirmCountUnsubRef = useRef(null);

  const [reloadBookingsKey, setReloadBookingsKey] = useState(0);

  const [showCancelled] = useState(false);

  const isIos = isIosCapacitor();
  const [iosAgendaDebug, setIosAgendaDebug] = useState(null);
  const recordIosAgendaDebug = (stage, payload = {}) => {
    const next = publishIosAgendaDebugV16D(stage, payload);
    try { setIosAgendaDebug(next); } catch (_) {}
    return next;
  };

  const actorForIsolation = getEffectiveActor(auth);
  const actorIsolationKey = `${actorForIsolation.uid || "no_uid"}:${actorForIsolation.email || "no_email"}`;
  const calendarIsolationKey = `${actorIsolationKey}:${businessIdProp || "no_business"}`;
  const previousCalendarIsolationKeyRef = useRef(calendarIsolationKey);

  useEffect(() => {
    const previous = previousCalendarIsolationKeyRef.current;
    if (previous === calendarIsolationKey) return;
    previousCalendarIsolationKeyRef.current = calendarIsolationKey;

    // Hard reset sensitive calendar state when the effective user/business scope changes.
    // This prevents user A's calendar/bookings from flashing for user B after logout/login.
    setBusinessDoc(null);
    setEmployees([]);
    setServices([]);
    setBookings([]);
    setSelectedEmployeeIds([]);
    setIsWorkerView(false);
    setWorkerEmailMode(null);
    setDetectedEmployeeDocId(null);
    setCurrentWorkerEmployee(null);
    setCurrentWorkerPermissions(DEFAULT_WORKER_PERMISSIONS);
    setBusinessPendingCount(0);
    setBusinessToConfirmCount(0);
    setWorkerPendingCount(0);
    setWorkerToConfirmCount(0);
    setEmployeeHeaderColors({});
    setLoading(true);

    try { if (pendingCountUnsubRef.current) pendingCountUnsubRef.current(); } catch (_) {}
    pendingCountUnsubRef.current = null;
    try { if (confirmCountUnsubRef.current) confirmCountUnsubRef.current(); } catch (_) {}
    confirmCountUnsubRef.current = null;
  }, [calendarIsolationKey]);

  const workerRouteParams = useMemo(() => {
    try {
      if (typeof window === "undefined") return { workerId: null, workerEmail: null };

      const params = new URLSearchParams(window.location.search || "");
      const workerId = params.get("worker") || params.get("workerId") || params.get("employeeId") || null;
      const workerEmailRaw = params.get("workerEmail") || params.get("employeeEmail") || null;

      return {
        workerId: workerId ? String(workerId) : null,
        workerEmail: workerEmailRaw ? norm(decodeURIComponent(workerEmailRaw)) : null,
      };
    } catch (_) {
      return { workerId: null, workerEmail: null };
    }
  }, [activeBusinessId]);

  const routeStateWorkerProfile = useMemo(
    () => workerProfileFromRouteState(routeState),
    [routeState]
  );

  // Auto-detect businessId for worker when none provided
  useEffect(() => {
    let mounted = true;

    async function detectBusinessForWorker() {
      if (activeBusinessId) return;

      try {
        const actor = getEffectiveActor(auth);
        const uid = actor.uid;
        const email = actor.email;

        if (!uid && !email) return;

        if (isIos) {
          const businesses = await fsClient.listCollectionByPath("businesses", {}, { db });
          for (const biz of businesses || []) {
            const bizId = biz?.id || biz?.businessId || null;
            if (!bizId) continue;

            let employeesList = [];
            try {
              employeesList = await fsClient.listCollectionByPath(`businesses/${bizId}/employees`, {}, { db });
            } catch (_) {
              employeesList = [];
            }

            const found = findMatchingEmployee(employeesList, actor);
            if (found && mounted) {
              setActiveBusinessId(String(bizId));
              return;
            }
          }
          return;
        }

        try {
          if (uid) {
            const cg = collectionGroup(db, "employees");
            const q1 = query(cg, where("userUid", "==", uid));
            const snap1 = await getDocs(q1);
            if (!snap1.empty && mounted) {
              const d = snap1.docs[0];
              const parts = d.ref.path.split("/");
              const bIdx = parts.indexOf("businesses");
              const found = bIdx >= 0 && parts.length > bIdx + 1 ? parts[bIdx + 1] : null;
              if (found) {
                setActiveBusinessId(found);
                return;
              }
            }
          }
        } catch (_) {}

        try {
          if (email) {
            const cg2 = collectionGroup(db, "employees");
            const q2 = query(cg2, where("email", "==", email));
            const snap2 = await getDocs(q2);
            if (!snap2.empty && mounted) {
              const d = snap2.docs[0];
              const parts = d.ref.path.split("/");
              const bIdx = parts.indexOf("businesses");
              const found = bIdx >= 0 && parts.length > bIdx + 1 ? parts[bIdx + 1] : null;
              if (found) {
                setActiveBusinessId(found);
                return;
              }
            }
          }
        } catch (_) {}
      } catch (e) {
        console.warn("[useAdminCalendarData] auto-detect business error", e);
      }
    }

    detectBusinessForWorker();
    return () => {
      mounted = false;
    };
  }, [activeBusinessId, auth, db, isIos]);

  // Employees load/subscription
  useEffect(() => {
    let mounted = true;
    let unsub = null;

    if (!activeBusinessId) {
      setEmployees([]);
      setSelectedEmployeeIds([]);
      setDetectedEmployeeDocId(null);
      setCurrentWorkerEmployee(null);
      setCurrentWorkerPermissions(DEFAULT_WORKER_PERMISSIONS);
      return () => {};
    }

    async function loadEmployeesIOS() {
      try {
        setLoading(true);
        const actor = getEffectiveActor(auth);
        const fallbackEmail = workerRouteParams.workerEmail || actor.email || null;
        const fallbackId = workerRouteParams.workerId || null;
        const workerTargetRequested = Boolean(isWorkerView || workerRouteParams.workerId || workerRouteParams.workerEmail || routeStateWorkerProfile);
        if (workerTargetRequested) {
          const accessProfile = await loadWorkerAccessProfileForBusiness(db, activeBusinessId, actor, fallbackEmail, fallbackId, routeStateWorkerProfile, auth);
          if (!mounted) return;
          // LOOP WORKER AGENDA IOS FIRST PAINT V10C
          // For iOS worker routes, do not wait on Firestore Web SDK employee
          // queries. The access/function profile is the source of truth and is
          // enough to release the first paint with the real worker resource.
          const mergedFallbackProfile = mergeWorkerProfilesForCalendar(accessProfile || {}, routeStateWorkerProfile || {});
          setEmployees(mergedFallbackProfile ? [mergedFallbackProfile] : []);
          if (mergedFallbackProfile) {
            const nextId = String(mergedFallbackProfile.id || mergedFallbackProfile.employeeId || fallbackId || "").trim();
            const nextEmail = norm(mergedFallbackProfile.email || mergedFallbackProfile.employeeEmail || mergedFallbackProfile.workerEmail || fallbackEmail || actor.email || "");
            setIsWorkerView(true);
            setWorkerEmailMode(nextEmail || null);
            setSelectedEmployeeIds(nextId ? [nextId] : []);
            setDetectedEmployeeDocId(nextId || null);
            setCurrentWorkerEmployee(mergedFallbackProfile);
            setCurrentWorkerPermissions(normalizeWorkerPermissions(mergedFallbackProfile));
          }
          try {
            if (typeof window !== "undefined") {
              window.__LOOP_WORKER_IOS_FIRST_PAINT_DEBUG__ = {
                stage: mergedFallbackProfile ? "ios_worker_profile_ready_v10c" : "ios_worker_profile_empty_v10c",
                businessId: activeBusinessId,
                workerId: mergedFallbackProfile?.id || mergedFallbackProfile?.employeeId || fallbackId || null,
                workerName: mergedFallbackProfile?.name || mergedFallbackProfile?.displayName || mergedFallbackProfile?.employeeName || null,
                hasAvailability: Boolean(mergedFallbackProfile?.availability || mergedFallbackProfile?.schedule || mergedFallbackProfile?.workingHours || mergedFallbackProfile?.hours),
                serviceCount: workerVisibleServicesFromProfiles(mergedFallbackProfile).length,
                at: new Date().toISOString(),
              };
            }
          } catch (_) {}
          return;
        }
        const list = await fsClient.listCollectionByPath(`businesses/${activeBusinessId}/employees`, {}, { db });
        if (!mounted) return;
        const normalized = Array.isArray(list) ? list.map((e) => ({ id: e.id || e.employeeId || null, ...(e || {}) })) : [];
        normalized.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
        if (normalized.length) {
          setEmployees(normalized);
        } else {
          // A successful empty team read is authoritative. Owner identity is not an employee.
          setEmployees([]);
        }
      } catch (err) {
        console.error("[useAdminCalendarData] employees load iOS error", err);
        if (mounted) {
          const actor = getEffectiveActor(auth);
          const accessProfile = await loadWorkerAccessProfileForBusiness(db, activeBusinessId, actor, workerRouteParams.workerEmail || actor.email || null, workerRouteParams.workerId || null, routeStateWorkerProfile, auth);
          const mergedFallbackProfile = mergeWorkerProfilesForCalendar(accessProfile || {}, routeStateWorkerProfile || {});
          setEmployees(mergedFallbackProfile ? [mergedFallbackProfile] : []);
          if (mergedFallbackProfile) {
            const fallbackId = workerRouteParams.workerId || null;
            const fallbackEmail = workerRouteParams.workerEmail || actor.email || null;
            const nextId = String(mergedFallbackProfile.id || mergedFallbackProfile.employeeId || fallbackId || "").trim();
            const nextEmail = norm(mergedFallbackProfile.email || mergedFallbackProfile.employeeEmail || mergedFallbackProfile.workerEmail || fallbackEmail || actor.email || "");
            setIsWorkerView(true);
            setWorkerEmailMode(nextEmail || null);
            setSelectedEmployeeIds(nextId ? [nextId] : []);
            setDetectedEmployeeDocId(nextId || null);
            setCurrentWorkerEmployee(mergedFallbackProfile);
            setCurrentWorkerPermissions(normalizeWorkerPermissions(mergedFallbackProfile));
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    function loadEmployeesWeb() {
      setLoading(true);
      const col = collection(db, `businesses/${activeBusinessId}/employees`);
      const qEmp = query(col, orderBy("name", "asc"));
      const actor = getEffectiveActor(auth);
      const fallbackEmail = workerRouteParams.workerEmail || actor.email || null;
      const fallbackId = workerRouteParams.workerId || null;

      const applySelfFallback = async (reason = "empty_or_denied") => {
        try {
          const accessProfile = await loadWorkerAccessProfileForBusiness(db, activeBusinessId, actor, fallbackEmail, fallbackId, routeStateWorkerProfile, auth);
          const selfRows = await loadSelfEmployeeRowsForBusiness(db, activeBusinessId, actor, fallbackEmail, fallbackId);
          if (!mounted) return false;
          if (selfRows.length) {
            const normalizedRows = selfRows
              .map((row) => mergeWorkerProfilesForCalendar(accessProfile || {}, routeStateWorkerProfile || {}, row) || row)
              .filter(Boolean);
            normalizedRows.sort((a, b) => String(a?.name || a?.employeeName || "").localeCompare(String(b?.name || b?.employeeName || "")));
            setEmployees(normalizedRows);
            const primaryProfile = normalizedRows[0] || null;
            if (primaryProfile) {
              const nextId = String(primaryProfile.id || primaryProfile.employeeId || fallbackId || "").trim();
              const nextEmail = norm(primaryProfile.email || primaryProfile.employeeEmail || primaryProfile.workerEmail || fallbackEmail || actor.email || "");
              setIsWorkerView(true);
              setWorkerEmailMode(nextEmail || null);
              setSelectedEmployeeIds(nextId ? [nextId] : []);
              setDetectedEmployeeDocId(nextId || null);
              setCurrentWorkerEmployee(primaryProfile);
              setCurrentWorkerPermissions(normalizeWorkerPermissions(primaryProfile));
            }
            setLoading(false);
            try { console.info("[useAdminCalendarData] loaded self employee fallback", { reason, count: normalizedRows.length, accessMirror: Boolean(accessProfile) }); } catch (_) {}
            return true;
          }
          const mergedFallbackProfile = mergeWorkerProfilesForCalendar(accessProfile || {}, routeStateWorkerProfile || {});
          if (mergedFallbackProfile && (mergedFallbackProfile.id || mergedFallbackProfile.email || mergedFallbackProfile.name || mergedFallbackProfile.employeeName)) {
            setEmployees([mergedFallbackProfile]);
            const nextId = String(mergedFallbackProfile.id || mergedFallbackProfile.employeeId || fallbackId || "").trim();
            const nextEmail = norm(mergedFallbackProfile.email || mergedFallbackProfile.employeeEmail || mergedFallbackProfile.workerEmail || fallbackEmail || actor.email || "");
            setIsWorkerView(true);
            setWorkerEmailMode(nextEmail || null);
            setSelectedEmployeeIds(nextId ? [nextId] : []);
            setDetectedEmployeeDocId(nextId || null);
            setCurrentWorkerEmployee(mergedFallbackProfile);
            setCurrentWorkerPermissions(normalizeWorkerPermissions(mergedFallbackProfile));
            setLoading(false);
            try { console.info("[useAdminCalendarData] loaded route/access worker fallback", { reason, id: mergedFallbackProfile.id, hasName: Boolean(mergedFallbackProfile.name), hasPhoto: Boolean(mergedFallbackProfile.photoUrl), accessMirror: Boolean(accessProfile) }); } catch (_) {}
            return true;
          }
        } catch (fallbackErr) {
          try { console.warn("[useAdminCalendarData] self employee fallback failed", fallbackErr); } catch (_) {}
        }
        return false;
      };

      const workerTargetRequested = Boolean(isWorkerView || workerRouteParams.workerId || workerRouteParams.workerEmail || routeStateWorkerProfile);
      if (workerTargetRequested) {
        applySelfFallback("worker_targeted_loader").then((usedFallback) => {
          if (!usedFallback && mounted) {
            setEmployees([]);
            setLoading(false);
          }
        });
        return;
      }

      unsub = onSnapshot(
        qEmp,
        async (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          if (!mounted) return;
          // Worker routes use their dedicated loader above. Do not invent a worker for a solo owner.
          setEmployees(list);
          setLoading(false);
        },
        async (err) => {
          console.error("[useAdminCalendarData] employees snapshot error", err);
          const usedFallback = await applySelfFallback("employees_snapshot_error");
          if (!usedFallback && mounted) {
            setEmployees([]);
            setLoading(false);
          }
        }
      );
    }

    if (isIos) loadEmployeesIOS();
    else loadEmployeesWeb();

    return () => {
      mounted = false;
      try {
        if (unsub) unsub();
      } catch (_) {}
    };
  }, [activeBusinessId, db, auth, isIos, isWorkerView, workerRouteParams.workerId, workerRouteParams.workerEmail, routeStateWorkerProfile, reloadBookingsKey]);

  // Load services
  useEffect(() => {
    let mounted = true;
    if (!activeBusinessId) {
      setServices([]);
      return;
    }

    const workerTargetRequested = Boolean(isWorkerView || workerRouteParams.workerId || workerRouteParams.workerEmail || routeStateWorkerProfile);
    const workerFallbackServices = workerVisibleServicesFromProfiles(
      currentWorkerEmployee,
      routeStateWorkerProfile,
      ...(Array.isArray(employees) ? employees : [])
    );

    if (workerTargetRequested) {
      // LOOP WORKER LOADER LOOP FIX V10B
      // Worker rules can deny businesses/{businessId}/services. Use the services
      // already hydrated in the worker profile from getWorkerCalendarProfileHttp.
      setServices(workerFallbackServices);
      try {
        if (typeof window !== "undefined") {
          window.__LOOP_ADMIN_CALENDAR_SERVICES_DEBUG__ = {
            at: new Date().toISOString(),
            businessId: activeBusinessId,
            source: "worker_profile_services_v10",
            workerFallbackCount: workerFallbackServices.length,
          };
        }
      } catch (_) {}
      return () => { mounted = false; };
    }

    (async () => {
      try {
        let list = [];
        if (isIos) {
          list = await fsClient.listCollectionByPath(`businesses/${activeBusinessId}/services`, {}, { db });
          list = Array.isArray(list) ? list.map((s) => ({ id: s.id || s.serviceId || null, ...(s || {}) })) : [];
        } else {
          const colRef = collection(db, `businesses/${activeBusinessId}/services`);
          const snap = await getDocs(colRef);
          list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        }
        if (mounted) setServices(list);
      } catch (e) {
        console.warn("[useAdminCalendarData] load services failed", e);
        if (mounted) setServices(workerFallbackServices);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [activeBusinessId, db, isIos, isWorkerView, workerRouteParams.workerId, workerRouteParams.workerEmail, routeStateWorkerProfile, currentWorkerEmployee, employees]);

  // Load business doc + colors
  useEffect(() => {
    let mounted = true;
    if (!activeBusinessId) {
      setBusinessDoc(null);
      setEmployeeHeaderColors({});
      return;
    }

    (async () => {
      try {
        let data = null;
        if (isIos) {
          data = await fsClient.getDocByPath(`businesses/${activeBusinessId}`, { db });
        } else {
          const dref = doc(db, "businesses", activeBusinessId);
          const snap = await getDoc(dref);
          data = snap && snap.exists() ? snap.data() : null;
        }

        if (!mounted) return;
        setBusinessDoc(data || null);
        setEmployeeHeaderColors(
          data && data.employeeHeaderColors && typeof data.employeeHeaderColors === "object"
            ? data.employeeHeaderColors
            : {}
        );
      } catch (e) {
        console.warn("[useAdminCalendarData] load businessDoc error", e);
        const workerTargetRequested = Boolean(isWorkerView || workerRouteParams.workerId || workerRouteParams.workerEmail || routeStateWorkerProfile || currentWorkerEmployee);
        if (mounted && workerTargetRequested) {
          const profile = currentWorkerEmployee || routeStateWorkerProfile || {};
          const syntheticBusinessDoc = {
            ...(profile.businessRegion || profile.raw?.businessRegion || {}),
            id: activeBusinessId,
            businessId: activeBusinessId,
            name: profile.businessName || profile.business?.name || profile.raw?.businessName || profile.raw?.business?.name || "",
            businessName: profile.businessName || profile.business?.businessName || profile.raw?.businessName || profile.raw?.business?.businessName || "",
            logoUrl: profile.businessLogoUrl || profile.businessLogo || profile.logoUrl || profile.business?.logoUrl || profile.raw?.businessLogoUrl || profile.raw?.business?.logoUrl || "",
            businessLogoUrl: profile.businessLogoUrl || profile.businessLogo || profile.logoUrl || profile.business?.logoUrl || profile.raw?.businessLogoUrl || profile.raw?.business?.logoUrl || "",
            calendarSettings: profile.calendarSettings || profile.businessCalendarSettings || profile.raw?.calendarSettings || profile.raw?.businessCalendarSettings || {},
            _syntheticWorkerBusinessDoc: true,
          };
          setBusinessDoc(syntheticBusinessDoc);
          setEmployeeHeaderColors({});
          try {
            if (typeof window !== "undefined") {
              window.__LOOP_ADMIN_CALENDAR_DATA_DEBUG__ = {
                ...(window.__LOOP_ADMIN_CALENDAR_DATA_DEBUG__ || {}),
                businessDocSource: "worker_synthetic_after_denied_v16",
                businessId: activeBusinessId,
                error: e?.message || String(e),
                at: new Date().toISOString(),
              };
            }
          } catch (_) {}
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [activeBusinessId, db, isIos, isWorkerView, workerRouteParams.workerId, workerRouteParams.workerEmail, routeStateWorkerProfile, currentWorkerEmployee]);

  // Autodetect worker among employees.
  // A user is a worker when their auth email/uid matches an employee document,
  // or when the route explicitly includes ?worker=<employeeId> / ?workerEmail=<email>.
  useEffect(() => {
    const actor = getEffectiveActor(auth);
    const forcedWorkerId = workerRouteParams.workerId ? String(workerRouteParams.workerId) : null;
    const forcedWorkerEmail = workerRouteParams.workerEmail ? norm(workerRouteParams.workerEmail) : null;

    if (!actor.uid && !actor.email && !forcedWorkerId && !forcedWorkerEmail) {
      setIsWorkerView(false);
      setWorkerEmailMode(null);
      setDetectedEmployeeDocId(null);
      setCurrentWorkerEmployee(null);
      setCurrentWorkerPermissions(DEFAULT_WORKER_PERMISSIONS);
      setSelectedEmployeeIds([]);
      return;
    }

    const routeForcesWorker = Boolean(forcedWorkerId || forcedWorkerEmail);
    const actorIsOwnerOrAdmin = isOwnerOrAdminForBusiness(businessDoc, actor);

    // Owner/admin must always see the full team when entering the normal agenda.
    // Important after UID-linking: an owner can also exist as an employee, but that
    // should not automatically collapse the calendar into worker view.
    if (actorIsOwnerOrAdmin && !routeForcesWorker) {
      setIsWorkerView(false);
      setWorkerEmailMode(null);
      setDetectedEmployeeDocId(null);
      setCurrentWorkerEmployee(null);
      setCurrentWorkerPermissions(DEFAULT_WORKER_PERMISSIONS);
      setSelectedEmployeeIds([]); // empty = all workers/resources
      return;
    }

    if (employees && employees.length > 0) {
      if (forcedWorkerId) {
        const foundByRouteId = employees.find((e) => String(e?.id || e?.employeeId || "") === forcedWorkerId);
        if (foundByRouteId) {
          const foundEmail = norm(foundByRouteId?.email || foundByRouteId?.employeeEmail || foundByRouteId?.workerEmail);
          setIsWorkerView(true);
          setWorkerEmailMode(foundEmail || forcedWorkerEmail || actor.email || null);
          const perms = normalizeWorkerPermissions(foundByRouteId);
          setSelectedEmployeeIds([String(foundByRouteId.id || forcedWorkerId)]);
          setDetectedEmployeeDocId(String(foundByRouteId.id || forcedWorkerId));
          setCurrentWorkerEmployee(foundByRouteId);
          setCurrentWorkerPermissions(perms);
          return;
        }

        const routeProfile = routeStateWorkerProfile && String(routeStateWorkerProfile.id || routeStateWorkerProfile.employeeId || "") === String(forcedWorkerId)
          ? { ...routeStateWorkerProfile, id: forcedWorkerId, employeeId: forcedWorkerId }
          : (routeStateWorkerProfile ? { ...routeStateWorkerProfile, id: forcedWorkerId, employeeId: forcedWorkerId } : null);
        setIsWorkerView(true);
        setWorkerEmailMode(forcedWorkerEmail || routeProfile?.email || actor.email || null);
        setSelectedEmployeeIds([forcedWorkerId]);
        setDetectedEmployeeDocId(forcedWorkerId);
        setCurrentWorkerEmployee(routeProfile);
        setCurrentWorkerPermissions(normalizeWorkerPermissions(routeProfile || {}));
        return;
      }

      if (forcedWorkerEmail) {
        const foundByRouteEmail = employees.find((e) => {
          const empEmails = employeeIdentityForCalendar(e).emails;
          return forcedWorkerEmail && empEmails.includes(forcedWorkerEmail);
        });

        if (foundByRouteEmail) {
          const foundEmail = norm(foundByRouteEmail?.email || foundByRouteEmail?.employeeEmail || foundByRouteEmail?.workerEmail);
          setIsWorkerView(true);
          setWorkerEmailMode(foundEmail || forcedWorkerEmail);
          const perms = normalizeWorkerPermissions(foundByRouteEmail);
          setSelectedEmployeeIds([String(foundByRouteEmail.id)]);
          setDetectedEmployeeDocId(String(foundByRouteEmail.id));
          setCurrentWorkerEmployee(foundByRouteEmail);
          setCurrentWorkerPermissions(perms);
          return;
        }

        const routeProfile = routeStateWorkerProfile || null;
        const routeId = String(routeProfile?.id || routeProfile?.employeeId || "").trim();
        setIsWorkerView(true);
        setWorkerEmailMode(forcedWorkerEmail);
        setDetectedEmployeeDocId(routeId || null);
        setCurrentWorkerEmployee(routeProfile);
        setCurrentWorkerPermissions(normalizeWorkerPermissions(routeProfile || {}));
        setSelectedEmployeeIds(routeId ? [routeId] : []);
        return;
      }

      const found = findMatchingEmployee(employees, actor);
      if (found) {
        const foundEmail = norm(found?.email || found?.employeeEmail || found?.workerEmail);
        setIsWorkerView(true);
        setWorkerEmailMode(foundEmail || actor.email || null);
        const perms = normalizeWorkerPermissions(found);
        setSelectedEmployeeIds([String(found.id)]);
        setDetectedEmployeeDocId(String(found.id));
        setCurrentWorkerEmployee(found);
        setCurrentWorkerPermissions(perms);
        return;
      }
    }

    if (routeStateWorkerProfile && (routeForcesWorker || routeStateWorkerProfile.id || routeStateWorkerProfile.email)) {
      const routeId = String(routeStateWorkerProfile.id || routeStateWorkerProfile.employeeId || forcedWorkerId || "").trim();
      const routeEmail = norm(routeStateWorkerProfile.email || routeStateWorkerProfile.employeeEmail || routeStateWorkerProfile.workerEmail || forcedWorkerEmail || actor.email || "");
      setIsWorkerView(true);
      setWorkerEmailMode(routeEmail || null);
      setSelectedEmployeeIds(routeId ? [routeId] : []);
      setDetectedEmployeeDocId(routeId || null);
      setCurrentWorkerEmployee(routeStateWorkerProfile);
      setCurrentWorkerPermissions(normalizeWorkerPermissions(routeStateWorkerProfile));
      return;
    }

    setIsWorkerView(false);
    setWorkerEmailMode(null);
    setDetectedEmployeeDocId(null);
    setCurrentWorkerEmployee(null);
    setCurrentWorkerPermissions(DEFAULT_WORKER_PERMISSIONS);
  }, [auth, employees, businessDoc, activeBusinessId, workerRouteParams.workerId, workerRouteParams.workerEmail, routeStateWorkerProfile]);

  // Pending counts
  useEffect(() => {
    try {
      if (pendingCountUnsubRef.current) pendingCountUnsubRef.current();
    } catch (_) {}
    pendingCountUnsubRef.current = null;

    try {
      if (confirmCountUnsubRef.current) confirmCountUnsubRef.current();
    } catch (_) {}
    confirmCountUnsubRef.current = null;

    if (!activeBusinessId) {
      setBusinessPendingCount(0);
      setBusinessToConfirmCount(0);
      setWorkerPendingCount(0);
      setWorkerToConfirmCount(0);
      return () => {};
    }

    let mounted = true;
    const workerBadgeEmployeeId = String(
      detectedEmployeeDocId ||
      workerRouteParams.workerId ||
      currentWorkerEmployee?.id ||
      currentWorkerEmployee?.employeeId ||
      (Array.isArray(selectedEmployeeIds) && selectedEmployeeIds.length === 1 ? selectedEmployeeIds[0] : "") ||
      ""
    ).trim();
    const workerCountScope = Boolean(workerBadgeEmployeeId);
    const pendingVariantsSet = new Set(["pending", "Pending", "PENDING", "pendiente"]);
    const confirmVariantsSet = new Set(["accepted", "Accepted", "ACCEPTED", "confirmed", "Confirmed"]);
    const isWorkerBooking = (booking) => bookingMatchesWorkerEmployeeIdOnly(booking, workerBadgeEmployeeId);
    const countWorkerStatus = (rows, variants) => {
      if (!workerCountScope) return 0;
      return (Array.isArray(rows) ? rows : []).filter((b) => variants.has(String(b?.status || "")) && isWorkerBooking(b)).length;
    };

    async function loadCountsIOS() {
      try {
        const allBookings = await fsClient.listCollectionByPath(`businesses/${activeBusinessId}/bookings`, {}, { db });
        if (!mounted) return;
        const arr = Array.isArray(allBookings) ? allBookings : [];
        setBusinessPendingCount(arr.filter((b) => pendingVariantsSet.has(String(b?.status || ""))).length);
        setBusinessToConfirmCount(arr.filter((b) => confirmVariantsSet.has(String(b?.status || ""))).length);
        setWorkerPendingCount(countWorkerStatus(arr, pendingVariantsSet));
        setWorkerToConfirmCount(countWorkerStatus(arr, confirmVariantsSet));
      } catch (e) {
        console.warn("[useAdminCalendarData] iOS counts load failed", e);
        if (mounted) {
          setBusinessPendingCount(0);
          setBusinessToConfirmCount(0);
          setWorkerPendingCount(0);
          setWorkerToConfirmCount(0);
        }
      }
    }

    if (isIos) {
      loadCountsIOS();
      return () => {
        mounted = false;
      };
    }

    const colRef = collection(db, `businesses/${activeBusinessId}/bookings`);
    const pendingVariants = ["pending", "Pending", "PENDING", "pendiente"];
    const confirmVariants = ["accepted", "Accepted", "ACCEPTED", "confirmed", "Confirmed"];

    try {
      const qPendingCount = query(colRef, where("status", "in", pendingVariants));
      pendingCountUnsubRef.current = onSnapshot(qPendingCount, (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setBusinessPendingCount(snap.size || 0);
        setWorkerPendingCount(workerCountScope ? rows.filter(isWorkerBooking).length : 0);
      });
    } catch (e) {
      console.warn("[useAdminCalendarData] subscribe pending count failed", e);
      setBusinessPendingCount(0);
      setWorkerPendingCount(0);
    }

    try {
      const qConfirmCount = query(colRef, where("status", "in", confirmVariants));
      confirmCountUnsubRef.current = onSnapshot(qConfirmCount, (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setBusinessToConfirmCount(snap.size || 0);
        setWorkerToConfirmCount(workerCountScope ? rows.filter(isWorkerBooking).length : 0);
      });
    } catch (e) {
      console.warn("[useAdminCalendarData] subscribe confirm count failed", e);
      setBusinessToConfirmCount(0);
      setWorkerToConfirmCount(0);
    }

    return () => {
      mounted = false;
      try {
        if (pendingCountUnsubRef.current) pendingCountUnsubRef.current();
      } catch (_) {}
      pendingCountUnsubRef.current = null;
      try {
        if (confirmCountUnsubRef.current) confirmCountUnsubRef.current();
      } catch (_) {}
      confirmCountUnsubRef.current = null;
    };
  }, [activeBusinessId, db, auth, isIos, reloadBookingsKey, isWorkerView, workerRouteParams.workerId, workerRouteParams.workerEmail, detectedEmployeeDocId, workerEmailMode, currentWorkerEmployee, selectedEmployeeIds]);

  // Bookings load/subscription
  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    async function setup() {
      setBookingsLoadError('');
      if (!activeBusinessId || !selectedDate) {
        setBookings([]);
        return;
      }
      setLoading(true);

      const actor = getEffectiveActor(auth);

      if (isWorkerView || workerRouteParams.workerId || workerRouteParams.workerEmail) {
        let refreshing = false;
        const refresh = async () => {
          if (refreshing || cancelled || document.visibilityState === 'hidden') return;
          refreshing = true;
          try {
            const profile = await loadWorkerCalendarProfileViaFunction({db, auth, fresh: true, businessId: activeBusinessId, employeeId: detectedEmployeeDocId || workerRouteParams.workerId || '', workerEmail: workerEmailMode || workerRouteParams.workerEmail || actor.email || ''});
            if (cancelled) return;
            if (!profile?.employeeId && !profile?.id) throw new Error('worker-access-unavailable');
            const permissions = normalizeWorkerPermissions(profile);
            setCurrentWorkerPermissions(prev => JSON.stringify(prev) === JSON.stringify(permissions) ? prev : permissions);
            if (Array.isArray(profile.calendarResources) && profile.calendarResources.length) {
              setEmployees(prev => JSON.stringify(prev) === JSON.stringify(profile.calendarResources) ? prev : profile.calendarResources);
            }
            const regionalBusiness = profile.businessRegion || businessDoc || {};
            const start = viewMode === 'month' ? calendarMonthStartForAgenda(selectedDate) : startOfWeek(selectedDate);
            const end = viewMode === 'month' ? calendarMonthEndExclusiveForAgenda(selectedDate) : endOfWeekExclusiveFromStart(start);
            const rows = await loadWorkerCalendarBookingsViaFunction({db, auth, businessId: activeBusinessId, employeeId: profile.employeeId || profile.id, workerEmail: actor.email || '', selectedEmployeeIds, start:wallToInstant(start,regionalBusiness), end:wallToInstant(end,regionalBusiness), selectedDate, viewMode});
            if (cancelled) return;
            if (!Array.isArray(rows)) throw new Error('worker-calendar-unavailable');
            setWorkerAccessError('');
            setBookings(rows);
          } catch (error) {
            if (!cancelled) {
              setBookings([]);
              setWorkerAccessError('No pudimos verificar tu acceso. Actualiza o consulta con el negocio.');
            }
          } finally {
            refreshing = false;
            if (!cancelled) setLoading(false);
          }
        };
        await refresh();
        if (cancelled) return;
        const timer = setInterval(refresh, 60000);
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        unsub = () => {clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh);};
        return;
      }

      if (isIos) {
        const routeWorkerRequested = Boolean(workerRouteParams.workerId || workerRouteParams.workerEmail);
        const effectiveWorkerId = String(detectedEmployeeDocId || workerRouteParams.workerId || currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || "").trim();
        const effectiveWorkerEmail = norm(workerEmailMode || workerRouteParams.workerEmail || currentWorkerEmployee?.email || currentWorkerEmployee?.employeeEmail || currentWorkerEmployee?.workerEmail || actor.email || "");
        const effectiveSelectedEmployeeIds = (Array.isArray(selectedEmployeeIds) && selectedEmployeeIds.length)
          ? selectedEmployeeIds
          : (effectiveWorkerId ? [effectiveWorkerId] : []);

        recordIosAgendaDebug("bookings_setup_preflight_v16d", {
          businessId: activeBusinessId,
          routeWorkerRequested,
          isWorkerView,
          detectedEmployeeDocId,
          routeWorkerId: workerRouteParams.workerId || null,
          routeWorkerEmail: workerRouteParams.workerEmail || null,
          effectiveWorkerId: effectiveWorkerId || null,
          effectiveWorkerEmail: effectiveWorkerEmail || null,
          selectedEmployeeIds: effectiveSelectedEmployeeIds,
          actorHasUid: Boolean(actor.uid),
          actorHasEmail: Boolean(actor.email),
          authHasCurrentUser: Boolean(auth?.currentUser),
        });

        let iosStart;
        let iosEnd;
        if (viewMode === "month") {
          iosStart = calendarMonthStartForAgenda(selectedDate);
          iosEnd = calendarMonthEndExclusiveForAgenda(selectedDate);
        } else if (viewMode === "weekCompany") {
          iosStart = startOfWeek(selectedDate);
          iosEnd = endOfWeekExclusiveFromStart(iosStart);
        } else if (isWorkerView || routeWorkerRequested) {
          iosStart = startOfWeek(selectedDate);
          iosEnd = endOfWeekExclusiveFromStart(iosStart);
        } else {
          iosStart = startOfDay(selectedDate);
          iosEnd = endOfDayExclusive(selectedDate);
        }

        if ((isWorkerView || routeWorkerRequested) && (effectiveWorkerId || effectiveWorkerEmail)) {
          const functionBookings = await loadWorkerCalendarBookingsViaFunction({
            db,
            auth,
            businessId: activeBusinessId,
            employeeId: effectiveWorkerId || "",
            workerEmail: effectiveWorkerEmail || "",
            selectedEmployeeIds: effectiveSelectedEmployeeIds,
            start: wallToInstant(iosStart, businessDoc || {}),
            end: wallToInstant(iosEnd, businessDoc || {}),
            selectedDate,
            viewMode,
            recordDebug: recordIosAgendaDebug,
          });
          if (cancelled) return;
          if (Array.isArray(functionBookings)) {
            const scopedSample = functionBookings.find((b) => b && (b.__workerCalendarEmployeeId || b.employeeId || b.resourceId || b.workerId)) || null;
            const scopedEmployeeId = String(scopedSample?.__workerCalendarEmployeeId || scopedSample?.employeeId || scopedSample?.resourceId || scopedSample?.workerId || effectiveWorkerId || "").trim();
            const scopedWorkerEmail = norm(scopedSample?.__workerCalendarWorkerEmail || scopedSample?.workerEmail || scopedSample?.employeeEmail || effectiveWorkerEmail || "");
            if (scopedEmployeeId) {
              const scopedProfilePatch = {
                id: scopedEmployeeId,
                employeeId: scopedEmployeeId,
                workerId: scopedEmployeeId,
                resourceId: scopedEmployeeId,
                email: scopedWorkerEmail || null,
                employeeEmail: scopedWorkerEmail || null,
                workerEmail: scopedWorkerEmail || null,
              };
              setIsWorkerView(true);
              setWorkerEmailMode(scopedWorkerEmail || effectiveWorkerEmail || null);
              setSelectedEmployeeIds([scopedEmployeeId]);
              setDetectedEmployeeDocId(scopedEmployeeId);
              setCurrentWorkerEmployee((prev) => mergeWorkerProfilesForCalendar(prev || {}, currentWorkerEmployee || {}, scopedProfilePatch) || scopedProfilePatch);
              setCurrentWorkerPermissions(normalizeWorkerPermissions(currentWorkerEmployee || scopedProfilePatch));
              setEmployees((prev) => {
                const current = Array.isArray(prev) ? prev : [];
                const rest = current.filter((emp) => {
                  const empId = String(emp?.id || emp?.employeeId || emp?.workerId || emp?.resourceId || "").trim();
                  const empEmail = norm(emp?.email || emp?.employeeEmail || emp?.workerEmail || "");
                  return empId !== scopedEmployeeId && (!scopedWorkerEmail || empEmail !== scopedWorkerEmail);
                });
                return [mergeWorkerProfilesForCalendar(currentWorkerEmployee || {}, scopedProfilePatch) || scopedProfilePatch, ...rest].slice(0, 20);
              });
            }

            setBookings(functionBookings);
            recordIosAgendaDebug("bookings_function_set_state_v16j", {
              businessId: activeBusinessId,
              effectiveWorkerId: effectiveWorkerId || null,
              effectiveWorkerEmail: effectiveWorkerEmail || null,
              scopedEmployeeId: scopedEmployeeId || null,
              scopedWorkerEmail: scopedWorkerEmail || null,
              count: functionBookings.length,
              eventResourceIds: Array.from(new Set(functionBookings.map((b) => String(b?.resourceId || b?.employeeId || b?.workerId || "").trim()).filter(Boolean))).slice(0, 10),
            });
            setLoading(false);
            return;
          }
        } else if ((isWorkerView || routeWorkerRequested) && !effectiveWorkerId) {
          recordIosAgendaDebug("bookings_no_effective_worker_id_v16d", {
            businessId: activeBusinessId,
            routeWorkerRequested,
            isWorkerView,
            detectedEmployeeDocId,
            routeWorkerId: workerRouteParams.workerId || null,
            currentWorkerEmployeeId: currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || null,
          });
        }

        try {
          recordIosAgendaDebug("bookings_rest_fallback_start_v16d", {
            businessId: activeBusinessId,
            effectiveWorkerId: effectiveWorkerId || null,
            routeWorkerRequested,
            isWorkerView,
          });
          const raw = await fsClient.listCollectionByPath(`businesses/${activeBusinessId}/bookings`, {}, { db });
          if (cancelled) return;
          const filtered = applyBookingsFilter(raw, {
            selectedDate,
            viewMode,
            isWorkerView: Boolean(isWorkerView || routeWorkerRequested),
            detectedEmployeeDocId: effectiveWorkerId || detectedEmployeeDocId,
            workerEmailMode: effectiveWorkerEmail || workerEmailMode,
            selectedEmployeeIds: effectiveSelectedEmployeeIds,
            businessDoc,
            actor,
            currentWorkerPermissions,
            currentWorkerEmployee: currentWorkerEmployee || (effectiveWorkerId ? { id: effectiveWorkerId, employeeId: effectiveWorkerId, email: effectiveWorkerEmail } : null),
          });

          let derivedWorkerProfileV16F = null;
          if ((isWorkerView || routeWorkerRequested) && !effectiveWorkerId && effectiveWorkerEmail) {
            derivedWorkerProfileV16F = deriveWorkerIdentityFromBookingsV16F(filtered, effectiveWorkerEmail, { requireEmailMatch: false }) ||
              deriveWorkerIdentityFromBookingsV16F(raw, effectiveWorkerEmail, { requireEmailMatch: true });

            if (derivedWorkerProfileV16F?.id) {
              let hydratedProfileV16F = mergeWorkerProfilesForCalendar(
                currentWorkerEmployee || {},
                routeStateWorkerProfile || {},
                derivedWorkerProfileV16F
              );

              try {
                const richProfileV16F = await loadWorkerCalendarProfileViaFunction({
                  db,
                  auth,
                  businessId: activeBusinessId,
                  employeeId: derivedWorkerProfileV16F.id,
                  workerEmail: effectiveWorkerEmail || derivedWorkerProfileV16F.email || derivedWorkerProfileV16F.employeeEmail || derivedWorkerProfileV16F.workerEmail || "",
                });
                if (richProfileV16F && typeof richProfileV16F === "object") {
                  hydratedProfileV16F = mergeWorkerProfilesForCalendar(hydratedProfileV16F, richProfileV16F);
                }
              } catch (_) {}

              const nextIdV16F = String(hydratedProfileV16F?.id || hydratedProfileV16F?.employeeId || derivedWorkerProfileV16F.id || "").trim();
              const nextEmailV16F = norm(hydratedProfileV16F?.email || hydratedProfileV16F?.employeeEmail || hydratedProfileV16F?.workerEmail || effectiveWorkerEmail || "");

              if (nextIdV16F) {
                setIsWorkerView(true);
                setWorkerEmailMode(nextEmailV16F || effectiveWorkerEmail || null);
                setSelectedEmployeeIds([nextIdV16F]);
                setDetectedEmployeeDocId(nextIdV16F);
                setCurrentWorkerEmployee({
                  ...(hydratedProfileV16F || {}),
                  id: nextIdV16F,
                  employeeId: nextIdV16F,
                  workerId: hydratedProfileV16F?.workerId || nextIdV16F,
                  resourceId: hydratedProfileV16F?.resourceId || nextIdV16F,
                  email: nextEmailV16F || hydratedProfileV16F?.email || null,
                  employeeEmail: nextEmailV16F || hydratedProfileV16F?.employeeEmail || null,
                  workerEmail: nextEmailV16F || hydratedProfileV16F?.workerEmail || null,
                });
                setCurrentWorkerPermissions(normalizeWorkerPermissions(hydratedProfileV16F));
                setEmployees((prev) => {
                  const current = Array.isArray(prev) ? prev : [];
                  const rest = current.filter((emp) => {
                    const empId = String(emp?.id || emp?.employeeId || "").trim();
                    const empEmail = norm(emp?.email || emp?.employeeEmail || emp?.workerEmail || "");
                    return empId !== nextIdV16F && (!nextEmailV16F || empEmail !== nextEmailV16F);
                  });
                  return [{
                    ...(hydratedProfileV16F || {}),
                    id: nextIdV16F,
                    employeeId: nextIdV16F,
                    workerId: hydratedProfileV16F?.workerId || nextIdV16F,
                    resourceId: hydratedProfileV16F?.resourceId || nextIdV16F,
                    email: nextEmailV16F || hydratedProfileV16F?.email || null,
                    employeeEmail: nextEmailV16F || hydratedProfileV16F?.employeeEmail || null,
                    workerEmail: nextEmailV16F || hydratedProfileV16F?.workerEmail || null,
                  }, ...rest].slice(0, 20);
                });

                recordIosAgendaDebug("ios_email_worker_identity_resolved_v16f", {
                  businessId: activeBusinessId,
                  derivedEmployeeId: nextIdV16F,
                  effectiveWorkerEmail,
                  hasPhoto: Boolean(hydratedProfileV16F?.photoUrl || hydratedProfileV16F?.workerPhotoUrl || hydratedProfileV16F?.employeePhotoUrl || hydratedProfileV16F?.avatarUrl),
                  hasAvailability: Boolean(hydratedProfileV16F?.availability || hydratedProfileV16F?.schedule || hydratedProfileV16F?.workingHours || hydratedProfileV16F?.hours),
                  filteredCount: Array.isArray(filtered) ? filtered.length : 0,
                  rawCount: Array.isArray(raw) ? raw.length : 0,
                });
              }
            }
          }

          setBookings(filtered);
          setBookingsLoadError('');
          recordIosAgendaDebug("bookings_rest_fallback_success_v16f", {
            businessId: activeBusinessId,
            rawCount: Array.isArray(raw) ? raw.length : 0,
            filteredCount: Array.isArray(filtered) ? filtered.length : 0,
            routeWorkerRequested,
            isWorkerView,
            effectiveWorkerId,
            effectiveWorkerEmail,
            selectedEmployeeIds: effectiveSelectedEmployeeIds,
            derivedWorkerId: derivedWorkerProfileV16F?.id || null,
            samples: (Array.isArray(filtered) ? filtered : []).slice(0, 8).map((b) => ({
              id: b.id || b.bookingId || null,
              status: b.status || null,
              employeeId: b.employeeId || b.workerId || b.resourceId || b.assignedEmployeeId || null,
              employeeEmail: b.employeeEmail || b.workerEmail || b.assignedEmployeeEmail || null,
              scheduledAt: b.scheduledAt || b.scheduledAtDate || b.date || null,
            })),
          });
          setLoading(false);
          return;
        } catch (e) {
          console.error("[useAdminCalendarData] bookings iOS load error", e);
          setBookingsLoadError('No pudimos cargar las reservas. Reintenta para comprobar la agenda.');
          recordIosAgendaDebug("bookings_rest_fallback_failed_v16d", {
            businessId: activeBusinessId,
            error: e?.message || String(e),
            routeWorkerRequested: Boolean(workerRouteParams.workerId || workerRouteParams.workerEmail),
            isWorkerView,
          });
          setBookings([]);
          setLoading(false);
          return;
        }
      }

      let start, end;
      if (viewMode === "month") {
        start = calendarMonthStartForAgenda(selectedDate);
        end = calendarMonthEndExclusiveForAgenda(selectedDate);
      } else if (viewMode === "weekCompany") {
        start = startOfWeek(selectedDate);
        end = endOfWeekExclusiveFromStart(start);
      } else if (isWorkerView) {
        start = startOfWeek(selectedDate);
        end = endOfWeekExclusiveFromStart(start);
      } else {
        start = startOfDay(selectedDate);
        end = endOfDayExclusive(selectedDate);
      }

      try {
        const colRef = collection(db, `businesses/${activeBusinessId}/bookings`);
        const baseClauses = [where("scheduledAt", ">=", wallToInstant(start, businessDoc || {})), where("scheduledAt", "<", wallToInstant(end, businessDoc || {}))];

        const userEmail = actor.email || "";
        const userUid = actor.uid;
        const isOwnerOrAdmin = isOwnerOrAdminForBusiness(businessDoc, actor);
        const clauses = [...baseClauses];
        const isNoWorkers = false;
        const workerPerms = normalizeWorkerPermissions(currentWorkerPermissions);

        if (isWorkerView) {
          // LOOP WORKER OWN-ONLY V5: do not add a narrow Firestore where here.
          // Bookings in production may store worker assignment under employeeId,
          // workerId, employeeEmail, assignedEmployees, etc. We load the date range
          // and enforce the worker filter in memory below before setBookings().
          if (!workerPerms.canViewOwnCalendar && !workerPerms.canViewAllCalendars) {
            clauses.push(where("employeeId", "==", "__no_access__"));
          }
        } else if (viewMode === "weekCompany") {
          // owner/company weekly view: no extra worker filters
        } else {
          if (!isOwnerOrAdmin && !isNoWorkers) {
            if (workerEmailMode) clauses.push(where("employeeEmail", "==", userEmail));
            else if (selectedEmployeeIds && selectedEmployeeIds.length > 0 && selectedEmployeeIds.length <= 10) {
              clauses.push(where("employeeId", "in", selectedEmployeeIds.map(String)));
            }
          }
        }

        const qSnap = query(colRef, ...clauses, orderBy("scheduledAt", "asc"));
        unsub = onSnapshot(
          qSnap,
          (snap) => {
            if (cancelled) return;
            const list = snap.docs.map((d) => {
              const data = d.data() || {};
              return { id: d.id, scheduledAtDate: bookingScheduledAtToDate(data), ...data };
            });
            const safeList = isWorkerView
              ? applyBookingsFilter(list, {
                  selectedDate,
                  viewMode,
                  isWorkerView,
                  detectedEmployeeDocId,
                  workerEmailMode,
                  selectedEmployeeIds,
                  businessDoc,
                  actor,
                  currentWorkerPermissions,
                  currentWorkerEmployee,
                })
              : list;
            setBookings(safeList);
            setBookingsLoadError('');
            setLoading(false);
          },
          (err) => {
            console.error("[useAdminCalendarData] bookings onSnapshot error", err);
            setBookingsLoadError('No pudimos cargar las reservas. Reintenta para comprobar la agenda.');
            setBookings([]);
            setLoading(false);
          }
        );
      } catch (e) {
        console.error("[useAdminCalendarData] bookings setup error", e);
        setBookingsLoadError('No pudimos cargar las reservas. Reintenta para comprobar la agenda.');
        setBookings([]);
        setLoading(false);
      }
    }

    setup();
    return () => {
      cancelled = true;
      try {
        if (unsub) unsub();
      } catch (_) {}
    };
  }, [
    activeBusinessId,
    selectedDate,
    selectedEmployeeIds,
    workerEmailMode,
    businessDoc,
    reloadBookingsKey,
    isWorkerView,
    detectedEmployeeDocId,
    currentWorkerPermissions,
    currentWorkerEmployee,
    viewMode,
    auth,
    db,
    isIos,
    workerRouteParams.workerId,
    workerRouteParams.workerEmail,
  ]);

  // Events mapping
  const allEvents = useMemo(() => {
    return (bookings || [])
      .map((b) => {
        try {
          const startDate =
            bookingScheduledAtToDate({...b, timezone: b.timezone || businessDoc?.timezone});
          if (!startDate || isNaN(startDate.getTime())) return null;

          if (!showCancelled && (b.status === "cancelled" || b.status === "Canceled" || b.status === "Cancelled")) return null;

          const duration = Number(b.duration || 60) || 60;
          const end = new Date(startDate.getTime() + duration * 60000);

          const serviceKey = (b.serviceId || b.serviceName || "default").toString();
          const accent = colorForService(serviceKey);

          return {
            id: b.id,
            title: b.serviceName ? `${b.serviceName}` : b.customerName || "Reserva",
            start: startDate.toISOString(),
            end: end.toISOString(),
            resourceId: resolveBookingResourceIdForCalendar(b, employees) || (isCalendarUnassignedBooking(b) ? "__unassigned__" : null),
            extendedProps: { raw: b, serviceKey, accent },
            backgroundColor: "#ffffff",
            borderColor: "rgba(2,6,23,0.10)",
            textColor: textBlack(),
            display: "block",
          };
        } catch (e) {
          console.warn("[useAdminCalendarData] allEvents mapping error", b && b.id, e);
          return null;
        }
      })
      .filter(Boolean);
  }, [bookings, showCancelled, employees, businessDoc]);

  const eventsForDay = useMemo(() => {
    const selSet = new Set((selectedEmployeeIds || []).map(String));
    const actor = getEffectiveActor(auth);
    return allEvents.filter((ev) => {
      const raw = ev?.extendedProps?.raw || ev?.raw || ev || {};
      if (isWorkerView) {
        if (raw.__workerCalendarFunctionScoped === true) return true;
        const workerPerms = normalizeWorkerPermissions(currentWorkerPermissions);
        if (workerPerms.canViewAllCalendars && selectedEmployeeIds && selectedEmployeeIds.length > 0) {
          if (!ev.resourceId || ev.resourceId === "__unassigned__") return false;
          if (selSet.has(String(ev.resourceId))) return true;
        }
        return bookingMatchesCurrentWorker(raw, actor, detectedEmployeeDocId, workerEmailMode, currentWorkerEmployee, selectedEmployeeIds);
      }
      if (selectedEmployeeIds && selectedEmployeeIds.length > 0) {
        if (!ev.resourceId || ev.resourceId === "__unassigned__") return false;
        if (!selSet.has(ev.resourceId)) return false;
      }
      return true;
    });
  }, [allEvents, selectedEmployeeIds, isWorkerView, auth, detectedEmployeeDocId, workerEmailMode, currentWorkerEmployee, currentWorkerPermissions]);

  return {
    activeBusinessId,
    setActiveBusinessId,
    businessDoc,
    loading,
    workerAccessError,
    bookingsLoadError,

    employees,
    services,
    bookings,

    selectedEmployeeIds,
    setSelectedEmployeeIds,
    isWorkerView,
    workerEmailMode,
    detectedEmployeeDocId,
    currentWorkerEmployee,
    currentWorkerPermissions,
    workerRouteParams,

    businessPendingCount,
    businessToConfirmCount,
    workerPendingCount,
    workerToConfirmCount,

    employeeHeaderColors,
    setEmployeeHeaderColors,

    allEvents,
    eventsForDay,

    reloadBookingsKey,
    setReloadBookingsKey,

    iosAgendaDebug,
  };
}
